import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { ConfirmationModal, Button as KxButton, Tooltip } from "@knowtex-ai/components";
import InlineFeedbackTrigger from "../Feedback/InlineFeedbackTrigger";
import clipboard from "../../assets/clipboard.svg";
import css from "./noteList.module.css";
import { haveSelectedCodesChanged, isReviewNoteDirty } from "./reviewNoteUnsaved";
import { useNavigate } from "react-router-dom";
import AddCodeModal from "./ICD10Workflow/AddCodeModal";
import CodeSuggestionsModal from "./ICD10Workflow/CodeSuggestionsModal";
import AlternativeCodeModal from "./ICD10Workflow/AlternativeCodeModal";
import DeleteCodeModal from "./ICD10Workflow/DeleteCodeModal";
import EMSection from "./E&M/E&M";
import { calculateLevels, calculateTimeBasedLevel } from "./E&M/emlevelsfcn";
import userJSON from "../../utils/userJSON";
import MacrosFeature from "./Macros/MacrosFeature";
import { Mic, Printer, Square } from "lucide-react";
import sendServerLogging from "../../utils/sendServerLogging.js";
import FeedbackModal from "../Feedback/FeedbackModal.js";
import NoteCitations from "../dashboard/NoteCitations/NoteCitations";
import { toast } from "react-toastify";
import windowMessenger from "../../utils/WindowMessenger.js";
import { useTutorial } from "../Tutorial/useTutorial";
import NoteTitleDropdown from "./NoteTitleDropdown";
import { Events, trackEvent } from "../../lib/analytics";
import { useCopyTracking } from "../../hooks/useCopyTracking";
import { useFeatureGate } from "../../hooks/useFeatureGate";
import { useDeepgramDictation } from "../../hooks/useDeepgramDictation";
import useNoteTitleFavorites from "../../hooks/useNoteTitleFavorites";
import { usePatientChatCapabilities, usePatientChatDraftSaver } from "../../hooks/usePatientChat";
import useTabListKeyboard from "../../hooks/useTabListKeyboard";
import useTabWrap from "../../hooks/useTabWrap";
import * as Sentry from "@sentry/react";
import { isStandaloneUser as getIsStandaloneUser } from "../../utils/standaloneUser";
import { setLauncherDock } from "../../utils/launcherDock";
import { resolveSpecialtyToDbValue } from "../../utils/specialties";
import { isUnchangedFromSnapshot, buildNotePayload } from "./notePayload";
import { getVitalsSectionFromNote, replaceVitalsSection } from "./noteVitals";
import AskKnowtexSidecar, {
  SMART_ASSIST_DEFAULT_WIDTH_PERCENT,
  useSmartAssistLayout,
} from "./AskKnowtexSidecar";
import SmartAssistPanel from "../smartAssist/hosts/SmartAssistPanel";
import { useSmartAssistPanelState } from "../../hooks/useProviderSettings";
import { MacrosContext } from "../../utils/MacrosContext";
import { mergeTelehealthAvailableMacros } from "../../utils/telehealthMacros";
import { buildDotPhraseMap } from "../../utils/dotPhraseMap";
import { useSharedMacrosQuery } from "../../hooks/useSharedMacros";
import { REVIEW_SMART_ASSIST_PROMPTS } from "./SmartAssistPromptTools";
import { insertNoteDictationText, syncNoteDictationAfterEdit } from "./noteDictation";
import {
  buildCarryForwardHighlightRanges,
  buildNoteHighlightRanges,
  hasCarryForwardHighlightSources,
} from "./carryForwardHighlights";
import { getSessionToken } from "../../lib/sessionToken";

const NOTE_UNSAVED_CHANGES_CONFIRM_MESSAGE =
  "You have unsaved changes. Are you sure you want to close without saving?";

const CARRY_FORWARD_HIGHLIGHT_DEBOUNCE_MS = 300;

const VVC_TEMPLATE_MARKERS = [
  "Visit conducted by synchronous telehealth",
  "The patient refused to provide their location and emergency contact information",
];

const getVvcPlaceholderRanges = (text) => {
  if (!text) return [];

  const ranges = [];
  let lineStart = 0;

  text.split("\n").forEach((line) => {
    const isVvcTemplate = VVC_TEMPLATE_MARKERS.some((marker) => line.includes(marker));

    if (isVvcTemplate) {
      Array.from(line.matchAll(/\([^()\n]+\)/g)).forEach((match) => {
        if (match[0] === "(POC)") return;

        const start = lineStart + match.index;
        ranges.push({
          start,
          end: start + match[0].length,
        });
      });
    }

    lineStart += line.length + 1;
  });

  return ranges;
};

const HIGHLIGHT_CLASSNAMES = {
  vvcPlaceholder: "rounded-sm bg-yellow-100 text-transparent outline outline-1 outline-amber-100",
  carryForward: "rounded-sm bg-yellow-70 text-transparent outline outline-1 outline-amber-100",
};

const renderPlaceholderHighlights = (text, ranges) => {
  if (!text || ranges.length === 0) return text;

  const segments = [];
  let position = 0;

  ranges.forEach((range) => {
    segments.push(text.slice(position, range.start));
    segments.push(
      <mark
        key={`${range.start}-${range.end}`}
        className={HIGHLIGHT_CLASSNAMES[range.kind] || HIGHLIGHT_CLASSNAMES.carryForward}
      >
        {text.slice(range.start, range.end)}
      </mark>
    );
    position = range.end;
  });

  segments.push(text.slice(position));
  return segments;
};

// Helper functions for date format conversion
const convertToInputFormat = (dateStr) => {
  if (!dateStr) return "";
  // If already in yyyy-MM-dd format, return as is
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  // Convert MM/dd/yyyy to yyyy-MM-dd
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
    const [month, day, year] = dateStr.split("/");
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }
  return "";
};

const convertFromInputFormat = (dateStr) => {
  if (!dateStr) return "";
  // Convert yyyy-MM-dd to MM/dd/yyyy
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split("-");
    return `${month}/${day}/${year}`;
  }
  return dateStr;
};

const parseChartHistorySafe = (rawChartHistory) => {
  if (!rawChartHistory) return {};
  if (typeof rawChartHistory === "string") {
    try {
      return JSON.parse(rawChartHistory);
    } catch (error) {
      console.warn("Failed to parse chart history in EditPatient:", error);
      return {};
    }
  }
  if (typeof rawChartHistory === "object") return rawChartHistory;
  return {};
};

const getLatestVitalsFromChartHistory = (rawChartHistory) => {
  const parsed = parseChartHistorySafe(rawChartHistory);
  const rawVitals = parsed?.vitals;
  if (rawVitals === null || rawVitals === undefined) return "";
  const normalized = typeof rawVitals === "string" ? rawVitals.trim() : String(rawVitals).trim();
  return normalized;
};

const hasVitalsFieldInChartHistory = (rawChartHistory) => {
  const parsed = parseChartHistorySafe(rawChartHistory);
  return Object.prototype.hasOwnProperty.call(parsed, "vitals");
};

const hasValidAppointmentId = (appointmentId) => {
  if (typeof appointmentId === "string") {
    return appointmentId.trim().length > 0;
  }
  return appointmentId !== null && appointmentId !== undefined;
};

const hasTodayDateInVitals = (vitalsText, referenceDate = new Date()) => {
  const normalizedVitals = typeof vitalsText === "string" ? vitalsText : String(vitalsText || "");
  if (!normalizedVitals.trim()) return false;

  const referenceYear = referenceDate.getFullYear();
  const referenceMonth = referenceDate.getMonth() + 1;
  const referenceDay = referenceDate.getDate();
  // Supports MM/DD/YYYY and YYYY-MM-DD style dates embedded in vitals lines
  // like: BP: 106/72 mm[Hg] (05/29/2026 11:59)
  const datePattern =
    /\b(?:(\d{1,2})[/-](\d{1,2})[/-](\d{4})|(\d{4})[/-](\d{1,2})[/-](\d{1,2}))\b/g;
  let match = datePattern.exec(normalizedVitals);

  while (match) {
    const month = Number(match[1] ?? match[5]);
    const day = Number(match[2] ?? match[6]);
    const year = Number(match[3] ?? match[4]);

    if (
      Number.isInteger(month) &&
      Number.isInteger(day) &&
      Number.isInteger(year) &&
      month >= 1 &&
      month <= 12 &&
      day >= 1 &&
      day <= 31 &&
      year === referenceYear &&
      month === referenceMonth &&
      day === referenceDay
    ) {
      return true;
    }

    match = datePattern.exec(normalizedVitals);
  }

  return false;
};

const noteHasReferenceDateInVitals = (noteText, referenceDate = new Date()) => {
  const vitalsSection = getVitalsSectionFromNote(noteText);
  return hasTodayDateInVitals(vitalsSection, referenceDate);
};

const getSelectedScheduleReferenceDate = (fallbackDate = new Date()) => {
  try {
    if (typeof window === "undefined" || !window.localStorage) {
      return fallbackDate;
    }

    const selectedDateRaw = window.localStorage.getItem("date");
    if (typeof selectedDateRaw !== "string" || !selectedDateRaw.trim()) {
      return fallbackDate;
    }

    const normalizedSelectedDate = selectedDateRaw.trim();
    const monthByName = {
      january: 0,
      february: 1,
      march: 2,
      april: 3,
      may: 4,
      june: 5,
      july: 6,
      august: 7,
      september: 8,
      october: 9,
      november: 10,
      december: 11,
    };
    const friendlyDateMatch = normalizedSelectedDate.match(
      /^(?:[A-Za-z]+,\s*)?([A-Za-z]+)\s+(\d{1,2}),\s*(\d{4})$/
    );
    if (friendlyDateMatch) {
      const monthName = String(friendlyDateMatch[1] || "").toLowerCase();
      const monthIndex = monthByName[monthName];
      const day = Number(friendlyDateMatch[2]);
      const year = Number(friendlyDateMatch[3]);

      if (
        Number.isInteger(monthIndex) &&
        Number.isInteger(day) &&
        Number.isInteger(year) &&
        day >= 1 &&
        day <= 31
      ) {
        const parsedFriendlyDate = new Date(year, monthIndex, day);
        if (
          parsedFriendlyDate.getFullYear() === year &&
          parsedFriendlyDate.getMonth() === monthIndex &&
          parsedFriendlyDate.getDate() === day
        ) {
          return parsedFriendlyDate;
        }
      }
    }

    const parsedDate = new Date(selectedDateRaw);
    if (Number.isNaN(parsedDate.getTime())) {
      return fallbackDate;
    }

    return parsedDate;
  } catch {
    return fallbackDate;
  }
};

const REVIEW_SMART_ASSIST_HELP_TEXT =
  'What you can ask Smart Assist. Rewrite note content: Rewrite the HPI into bullets or Expand the PLAN with more detail. Make the note more concise: Make this note more concise. Find and replace text: Find and replace "hypertension" with "high blood pressure".';

const REVIEW_SMART_ASSIST_HELP_CONTENT = (
  <div className="text-left font-normal">
    <div className="font-semibold">What you can ask Smart Assist</div>
    <div className="mt-2 space-y-2">
      <p className="mb-0">
        Rewrite note content: `Rewrite the HPI into bullets` or `Expand the PLAN with more detail`.
      </p>
      <p className="mb-0">Make the note more concise: `Make this note more concise`.</p>
      <p className="mb-0">
        Find and replace text: `Find and replace "hypertension" with "high blood pressure"`.
      </p>
    </div>
  </div>
);

const REVIEW_SMART_ASSIST_EMPTY_STATE = {
  title: "Post Note Editing",
  body: "Smart Assist can modify or reformat this note, pull in information from the transcript, patient summary, and Smart Chart data objects, or remove and replace information.",
};

//Allows you to edit the patient and note associated with the patient.
const NAV_TAB_LOG_NAMES = {
  home: "Note",
  diarized: "Transcript",
  citations: "Note Citations",
  summary: "Patient Visit Summary",
};

const EditPatient = ({
  patient,
  isCrossSiteCare = false,
  isTutorialReviewAnchor = false,
  patientsChange,
  setPatientsChange,
  org,
  superOrg,
  providerSpecialty,
  isEditing,
  setIsEditing,
  name,
  generateRecordLink,
  status,
  ehrUploadStatus,
  submittingPatientId, // ✅ Change from isSubmittingToEHR
  setSubmittingPatientId, // ✅ Change from setIsSubmittingToEHR
  identifier,
  noteTitlesEntries = [],
  noteTitlesFavorites = [],
  setNoteTitlesFavorites,
  noteTitleOptionValues = [],
  noteTitleOptionValueSet,
  isNoteTitlesEnabled = false,
}) => {
  const { userMacros } = useContext(MacrosContext) || {};
  const saveUploadDisabledReasonId = useId();
  const isStandaloneUser = getIsStandaloneUser();
  const isCancelledPatient =
    String(patient.patient_type || "").toLowerCase() === "cancelled" ||
    String(patient.status || "").toLowerCase() === "cancelled";
  const isReadOnlyCancelled = isCancelledPatient && !isCrossSiteCare;
  const crossSiteRelaunchMessage = isCrossSiteCare
    ? `Relaunch Knowtex from ${patient.care_site?.facilityName || "the patient's scheduled facility"}${patient.care_site?.displayName ? ` (${patient.care_site.displayName})` : ""} before refreshing vitals or other station-dependent EHR data.`
    : "";
  //when we edit the text we update the database
  const [sendToEHR, setSendToEHR] = useState(false);
  const [userId, setUserId] = useState(patient.user_id);
  const noteTitleFavoritesMutation = useNoteTitleFavorites();

  // Captures the patient_id at modal-open time so that save operations
  // always target the correct patient even if polling re-renders the list.
  const openedPatientIdRef = useRef(patient.patient_id);
  // Used to track initial values and detect actual edits on save (should be replaced by a dirty flag from library at some point)
  const loadedSnapshotRef = useRef({});

  const [codesChange, setCodesChange] = useState(false);
  const [showUnspecifiedAlert, setShowUnspecifiedAlert] = useState(false);

  // state variable to show code suggestions modal
  const [showSuggestions, setShowSuggestions] = useState(false);

  //state variable to show add code modal
  const [showAddCode, setShowAddCode] = useState(false);

  const [currentSelectedCode, setCurrentSelectedCode] = useState(null);
  const [historicalCodes, setHistoricalCodes] = useState([]);

  const [isPrimaryCode, setIsPrimaryCode] = useState(false);
  const [isSecondaryCode, setIsSecondaryCode] = useState(false);

  const [userRationale, setUserRationale] = useState("");
  const [mdm_suggested, setMdmSuggestion] = useState("");
  const [mdm_selected, setMDMSelected] = useState("");
  const [mdm_selectedTs, setMDMSelectedTs] = useState(null);
  //state var to display alert for E&M rationale
  const [isRationaleProvided, setIsRationaleProvided] = useState(false);
  const [finalMDMCode, setFinalMDMCode] = useState(null);
  const [finalSelectedLevel, setFinalSelectedLevel] = useState(null);
  const [finalSelectedTs, setFinalSelectedTs] = useState(null);

  const [selectLevelAlert, setSelectLevelAlert] = useState(false);
  const [addRationaleAlert, setAddRationaleAlert] = useState(false);
  const [showRationale, setShowRationale] = useState(false);
  const [recommendedLevel, setRecommendedLevel] = useState(null);

  const [emData, setEMData] = useState(null);
  const [originalEmData, setOriginalEmData] = useState(null);
  const [timeSpentChanged, setTimeSpentChanged] = useState(false);
  const [activeNoteDictationTarget, setActiveNoteDictationTarget] = useState(null);
  const [savedDbNote, setSavedDbNote] = useState(patient.note || "");
  const noteDictationInterimRangeRef = useRef(null);
  const noteDictationSelectionRef = useRef({ start: 0, end: 0 });
  const noteDictationTargetRef = useRef(null);
  const smartAssistStateCacheRef = useRef(null);
  const [smartAssistDictationState, setSmartAssistDictationState] = useState({
    isDictating: false,
    isStarting: false,
  });

  const patientModalRef = useRef(null);
  const modalTitleRef = useRef(null);
  // Hidden element used to dismiss the Bootstrap modal programmatically.
  const modalDismissRef = useRef(null);
  // Set true right before a programmatic/button close so the Escape (`hide.bs.modal`)
  // guard doesn't prompt again — those paths run their own confirmation (or follow a save).
  // Non-consuming: cleared when the modal is shown/hidden, so a duplicate hide event
  // in the same close can't slip past it.
  const noteCloseConfirmedRef = useRef(false);
  // Holds the latest values the unsaved-note guard needs, so the modal event
  // listener (attached once) always reads current state.
  const reviewUnsavedGuardRef = useRef({
    status,
    note: "",
    savedNote: "",
    isReadOnlyCancelled: false,
  });
  // Controls the component-library confirmation modal shown when a close is
  // attempted with unsaved note changes. `pendingReviewCloseRef` holds the
  // action to run once the clinician confirms discarding their edits.
  const [unsavedCloseConfirmOpen, setUnsavedCloseConfirmOpen] = useState(false);
  const pendingReviewCloseRef = useRef(null);
  const dismissBootstrapModal = () => {
    noteCloseConfirmedRef.current = true;
    modalDismissRef.current?.click();
  };
  const hasUnsavedReviewNote = () => isReviewNoteDirty(reviewUnsavedGuardRef.current);
  // Route every review-modal close through the confirmation modal when there are
  // unsaved edits; otherwise run the close immediately.
  const requestReviewClose = (proceed) => {
    if (hasUnsavedReviewNote()) {
      pendingReviewCloseRef.current = proceed;
      setUnsavedCloseConfirmOpen(true);
      return;
    }
    proceed();
  };
  const handleUnsavedCloseContinue = () => {
    setUnsavedCloseConfirmOpen(false);
    const proceed = pendingReviewCloseRef.current;
    pendingReviewCloseRef.current = null;
    proceed?.();
  };
  const handleUnsavedCloseCancel = () => {
    setUnsavedCloseConfirmOpen(false);
    pendingReviewCloseRef.current = null;
  };
  const isPatientModalOpenRef = useRef(false);
  const isSidecarRenderedRef = useRef(false);
  const [isPatientModalOpen, setIsPatientModalOpen] = useState(false);
  const {
    isExpanded: isPatientChatSidecarExpanded,
    setIsExpanded: setIsPatientChatSidecarExpanded,
    setPreferredExpanded: setPatientChatSidecarPreferredExpanded,
  } = useSmartAssistPanelState();
  const [patientChatWidthPercent, setPatientChatWidthPercent] = useState(
    SMART_ASSIST_DEFAULT_WIDTH_PERCENT
  );
  const { contentScale: patientChatContentScale, isSplitView: isPatientChatSplitView } =
    useSmartAssistLayout();
  const [patientSummary, setPatientSummary] = useState("");
  const patientSummaryRef = useRef(patientSummary);
  useEffect(() => {
    patientSummaryRef.current = patientSummary;
  }, [patientSummary]);
  const showPatientSummary = org.startsWith("VA") || org === "Knowtex";
  const isPsychologySpecialty = resolveSpecialtyToDbValue(providerSpecialty) === "Psychology";
  const [copiedSummary, setCopiedSummary] = useState(false);
  const [copyAnnouncement, setCopyAnnouncement] = useState("");
  const copyAnnouncementTimerRef = useRef(null);
  const copyStatusTimerRef = useRef(null);
  const setPatientSummaryFromDotPhrase = useCallback((updater) => {
    setPatientSummary(updater);
    setCopiedSummary(false);
  }, []);

  const announceCopySuccess = useCallback((message) => {
    setCopyAnnouncement("");
    if (copyAnnouncementTimerRef.current) {
      clearTimeout(copyAnnouncementTimerRef.current);
    }
    copyAnnouncementTimerRef.current = setTimeout(() => {
      setCopyAnnouncement(message);
    }, 50);
  }, []);

  useEffect(() => {
    return () => {
      if (copyAnnouncementTimerRef.current) {
        clearTimeout(copyAnnouncementTimerRef.current);
      }
      if (copyStatusTimerRef.current) {
        clearTimeout(copyStatusTimerRef.current);
      }
    };
  }, []);

  const handlePrintSummary = () => {
    if (!patientSummary) return;

    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>Patient Visit Summary</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
              white-space: pre-wrap;
            }
          </style>
        </head>
        <body>
          ${patientSummary.replace(/\n/g, "<br>")}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const [chartHistory, setChartHistory] = useState([]);

  const [originalNote, setOriginalNote] = useState(null);
  useEffect(() => {
    setOriginalNote(patient.note);
  }, [patient.note]);

  useEffect(() => {
    setSavedDbNote(patient.note || "");
  }, [patient.patient_id, patient.note]);

  const calculateFinalMDMCode = (selectedLevel) => {
    // Get visit type modifier (0 for new, 1 for established)
    let visitValue;
    if (patient.patient_type === null || patient.patient_type === "new") {
      visitValue = 0;
    } else {
      visitValue = 1;
    }

    // Calculate the CPT code (992 + visit type + level)
    const cptCode = `992${visitValue}${selectedLevel}`;

    return cptCode;
  };

  useEffect(() => {
    // console.log("recalculating code");
    setFinalMDMCode(calculateFinalMDMCode(finalSelectedLevel));
  }, [finalSelectedLevel]);

  const { isActive: isTutorialActive, currentStepId } = useTutorial();
  const isMockPatient = patient.description === "QQQVAINTERFACE,IFC" && isTutorialActive;

  const [activeNavTab, setActiveNavTab] = useState("home");

  const STEP_TO_TAB = {
    review_note_page: "home",
    transcript_page: "diarized",
    note_citations: "citations",
    patient_visit_summary_page: "summary",
  };

  const effectiveNavTab =
    isTutorialActive && STEP_TO_TAB[currentStepId] ? STEP_TO_TAB[currentStepId] : activeNavTab;

  const getTabButtonClass = (tabKey) =>
    effectiveNavTab === tabKey
      ? isTutorialActive
        ? "bg-accent1 !text-white border-light-grey"
        : "active border-light-grey bg-accent1 !text-background1"
      : "border bg-secondary-grey text-accent1";

  const isCurrentCustomer = ["Knowtex", "VA"];

  const dontShowCodes = [];

  const navigate = useNavigate();

  const redirectToSettings = () => {
    navigate("/settings");
  };

  //json structure to send to backend
  const mdmSelectedObj = {
    original_selection: mdm_selected,
    original_selection_ts: mdm_selectedTs,
    knowtex_recommendation: mdm_suggested,
    final_level: finalSelectedLevel,
    final_code: finalMDMCode,
    final_selection_ts: finalSelectedTs,
    override_rationale: userRationale,
  };

  const updateProviderReviewed = async (id, { force = false, refreshList = true } = {}) => {
    if (!force && patient.provider_reviewed?.did_view) {
      console.log("Provider already viewed note.");
      return;
    }
    try {
      const providerReviewedData = {
        did_view: true,
        timestamp: Math.floor(Date.now() / 1000),
      };

      const payload = {
        id,
        providerReviewedData,
      };

      //debugging console log
      // console.log("provider review updated", payload);

      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");
      myHeaders.append("token", getSessionToken());

      await fetch(`${process.env.REACT_APP_API_URL}/notes/todos/${id}/provider-viewed`, {
        method: "PUT",
        headers: myHeaders,
        body: JSON.stringify(payload),
      });
      if (refreshList) {
        setPatientsChange((prev) => !prev);
      }
    } catch (error) {
      console.error("Failed to update provider reviewed", error);
    }
  };

  const saveNoteOnly = async (id) => {
    try {
      const snapshot = loadedSnapshotRef.current || {};
      const payload = buildNotePayload(
        {
          note,
          patientSummary,
          LOSCode,
          codingInfo,
          encounterTime,
          timeBasedCode,
          icd_10_codes,
          orders_v2: ordersV2,
          hcc_codes,
          cpt_codes,
          mdmSelectedObj,
          macros,
          userFeedback,
        },
        snapshot
      );

      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");
      myHeaders.append("token", getSessionToken());

      await fetch(`${process.env.REACT_APP_API_URL}/notes/todos/${id}`, {
        method: "PUT",
        headers: myHeaders,
        body: JSON.stringify(payload),
      });

      setSavedDbNote(note || "");

      // Save *selectedCodes* to DB
      await saveSelectedCodes(selectedCodes);

      setPatientsChange(true);

      //server logging
      sendServerLogging({
        body: {
          ...userJSON(localStorage.getItem("userName")),
          event: "UPLOAD_SAVE_NOTE",
          eventData: { mrn: patient.mrn },
          patientId: patient.patient_id,
        },
      });
    } catch (err) {
      console.error(err.message);
      toast.error("Failed to save note");
    }
  };

  const submitToVAAndLock = async () => {
    // Check if THIS patient is already submitting
    if (submittingPatientId === patient.patient_id) return false;

    if (isCrossSiteCare) {
      toast.error(
        "Save & Upload to EHR is disabled because this patient is scheduled at another facility."
      );
      return false;
    }

    if (isStandaloneUser) {
      toast.info("EHR upload is disabled for standalone users.");
      return false;
    }

    if (!ensureNoteTitleSelection()) {
      return false;
    }

    // Set THIS patient as submitting
    setSubmittingPatientId(patient.patient_id);

    let submissionSucceeded = false;
    try {
      // 1. Save the note first.
      const didSave = await editText(patient.patient_id, null, {
        refreshList: false,
      });
      if (!didSave) {
        return false;
      }

      // 2. Set uploading status
      await changeEHRStatusToUploading();

      // 3. Submit to VA FHIR
      const response = await fetch(`${process.env.REACT_APP_API_URL}/va/patient/submit-note`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          token: getSessionToken(),
        },
        body: JSON.stringify({
          patient_id: patient.patient_id,
          va_fhir_endpoint: localStorage.getItem("va_fhir_server"),
          duz: localStorage.getItem("va_duz"),
          ...(ienForSubmit ? { ien: ienForSubmit } : {}),
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        // Submission failed - revert status
        await changeEHRStatusToIdle();

        // ✅ NEW: Send Slack notification for failure
        await sendSlackNotification("VA_NOTE_SUBMISSION_FAILED", {
          mrn: patient.mrn,
          patientId: patient.patient_id,
          appointmentId: patient.appointment_id,
          error: data.details || data.error || "Unknown error",
        });

        toast.error(
          data.error ||
            "Your note failed to upload to the EHR. Please try again - if the issue persists, please reach out to support."
        );
        return false;
      }

      // 4. Success! Backend has set status to "Locked"
      submissionSucceeded = true;
      toast.success("Note submitted successfully to VA!");
      trackNoteFinalized("va_fhir");

      // ✅ NEW: Send Slack notification for success
      await sendSlackNotification("VA_NOTE_SUBMISSION", {
        mrn: patient.mrn,
        patientId: patient.patient_id,
        appointmentId: patient.appointment_id,
      });

      // Refresh patient list to show updated status
      setPatientsChange(true);
    } catch (err) {
      console.error("VA submission error:", err);

      // ✅ NEW: Send Slack notification for error
      await sendSlackNotification("VA_NOTE_SUBMISSION_FAILED", {
        mrn: patient.mrn,
        patientId: patient.patient_id,
        appointmentId: patient.appointment_id,
        error: err.message,
      });

      // Revert EHR status on error
      await changeEHRStatusToIdle();
    } finally {
      // Clear THIS patient from submitting state
      setSubmittingPatientId(null);
    }

    return submissionSucceeded;
  };

  const sendClickTextAreaLog = (mrn, patientId) => {
    //server logging
    sendServerLogging({
      body: {
        ...userJSON(localStorage.getItem("userName")),
        event: "CLICK_NOTE_TEXT_AREA",
        eventData: {
          mrn: mrn,
          patientId: patientId,
        },
      },
    });
  };

  const editText = async (id, noteOverride = null, { refreshList = true } = {}) => {
    // calculate final MDM code
    setFinalMDMCode(calculateFinalMDMCode(finalSelectedLevel));

    if (!ensureNoteTitleSelection()) {
      return false;
    }

    if (openedPatientIdRef.current !== id) {
      Sentry.captureException(
        new Error(
          `editText: patient ID mismatch — opened ${openedPatientIdRef.current}, saving ${id}. Aborting to prevent data corruption.`
        )
      );
      return false;
    }

    try {
      const effectiveNote = noteOverride !== null ? noteOverride : note;
      const snapshot = loadedSnapshotRef.current || {};
      const body = buildNotePayload(
        {
          note: effectiveNote,
          patientSummary,
          LOSCode,
          codingInfo,
          encounterTime,
          timeBasedCode,
          icd_10_codes,
          orders_v2: ordersV2,
          hcc_codes,
          cpt_codes,
          mdmSelectedObj,
          macros,
          userFeedback,
        },
        snapshot
      );

      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");
      myHeaders.append("token", getSessionToken());

      // Then save the note
      const response = await fetch(`${process.env.REACT_APP_API_URL}/notes/todos/${id}`, {
        method: "PUT",
        headers: myHeaders,
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        let errorMessage = `Failed to save note (${response.status})`;
        try {
          const errorData = await response.json();
          if (errorData?.error || errorData?.message) {
            errorMessage = errorData.error || errorData.message;
          }
        } catch {
          // Response body may not be JSON; keep the status-based message.
        }
        toast.error(errorMessage);
        return false;
      }

      // Save selected codes
      const codesSaved = await saveSelectedCodes(selectedCodes);
      if (!codesSaved) {
        toast.error("Note saved, but diagnosis codes could not be updated. Please try again.");
        return false;
      }

      // Update e_and_m JSON if time spent changed and affected selection basis
      if (timeSpentChanged && emData && originalEmData) {
        // Calculate time-based level to determine selection basis
        const timeLevel = calculateTimeBasedLevel(emData.time, emData.patient_type);
        const mdmLevel = calculateLevels(emData.mdm)?.note;

        // Determine selection basis: use time if time level is higher, otherwise use mdm
        const selectionBasis = timeLevel && mdmLevel && timeLevel > mdmLevel ? "time" : "mdm";

        // Determine the current effective level (time-based or MDM-based)
        const currentLevel = selectionBasis === "time" ? timeLevel : mdmLevel;

        // Get original level for comparison
        const originalTimeLevel = calculateTimeBasedLevel(
          originalEmData.time,
          originalEmData.patient_type
        );
        const originalMdmLevel = calculateLevels(originalEmData.mdm)?.note;
        const originalSelectionBasis = originalEmData.selection_on || "mdm";
        const originalLevel =
          originalSelectionBasis === "time" ? originalTimeLevel : originalMdmLevel;

        const updatedEmData = {
          ...emData,
          updated_time_spent: emData.time,
          new_selection_on: selectionBasis,
          updated_level: currentLevel !== originalLevel ? currentLevel : undefined,
        };

        try {
          const emResponse = await fetch(
            `${process.env.REACT_APP_API_URL}/notes/todos/${id}/em-data`,
            {
              method: "PATCH",
              headers: myHeaders,
              body: JSON.stringify(updatedEmData),
            }
          );
          if (!emResponse.ok) {
            throw new Error(`Failed to update E&M data (${emResponse.status})`);
          }
        } catch (err) {
          console.error("Error updating e_and_m data:", err.message);
          toast.error("Note saved, but E&M data could not be updated. Please try again.");
          return false;
        }

        setTimeSpentChanged(false);
      }

      // Only mark the note clean after every follow-up write succeeds so
      // Close / Escape still warn if codes or E&M did not persist.
      setSavedDbNote((noteOverride !== null ? noteOverride : note) || "");

      if (refreshList) {
        setPatientsChange(true);
      }

      return true;
    } catch (err) {
      console.error(err.message);
      return false;
    }
  };

  const saveNoteWithOverride = async (updatedNote) => {
    setNote(updatedNote);
    return editText(patient.patient_id, updatedNote);
  };

  const changeEHRStatusToUploading = async () => {
    try {
      const ehr_status = "uploading"; // Changed from ehrStatus to ehr_status
      const body = { ehr_status }; // Ensure the key matches the backend

      const myHeaders = new Headers();

      myHeaders.append("Content-Type", "application/json");
      myHeaders.append("token", getSessionToken());

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/notes/ehr_upload_status/${patient.patient_id}`,
        {
          method: "PUT",
          headers: myHeaders,
          body: JSON.stringify(body),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Failed to update EHR status:", errorText);
        return;
      }

      // console.log("EHR Upload Status changed to 'uploading'");
    } catch (err) {
      console.error("Error in changeEHRStatusToUploading:", err.message);
    }
  };

  const submitNoteToVA = async (patientId) => {
    if (isStandaloneUser) {
      throw new Error("EHR upload is disabled for standalone users.");
    }

    try {
      if (!ensureNoteTitleSelection()) {
        throw new Error("Note title selection is required");
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/va/patient/submit-note`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          token: getSessionToken(),
        },
        body: JSON.stringify({
          patient_id: patientId,
          va_fhir_endpoint: localStorage.getItem("va_fhir_server"),
          duz: localStorage.getItem("va_duz"),
          ...(ienForSubmit ? { ien: ienForSubmit } : {}),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.details || "Submission failed");
      }

      const result = await response.json();

      console.log("Note submitted successfully:", result);

      return result;
    } catch (error) {
      console.error("VA submission failed:", error);
      throw error;
    }
  };

  const changeEHRStatusToSuccess = async () => {
    try {
      const ehr_status = "success"; // Changed from ehrStatus to ehr_status
      const body = { ehr_status }; // Ensure the key matches the backend

      const myHeaders = new Headers();

      myHeaders.append("Content-Type", "application/json");
      myHeaders.append("token", getSessionToken());

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/notes/ehr_upload_status/${patient.patient_id}`,
        {
          method: "PUT",
          headers: myHeaders,
          body: JSON.stringify(body),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Failed to update EHR status:", errorText);
        return;
      }

      // console.log("EHR Upload Status changed to 'success'");
    } catch (err) {
      console.error("Error in changeEHRStatusToSuccess:", err.message);
    }
  };

  const changeEHRStatusToIdle = async () => {
    try {
      const ehr_status = "idle"; // Changed from ehrStatus to ehr_status
      const body = { ehr_status }; // Ensure the key matches the backend

      const myHeaders = new Headers();

      myHeaders.append("Content-Type", "application/json");
      myHeaders.append("token", getSessionToken());

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/notes/ehr_upload_status/${patient.patient_id}`,
        {
          method: "PUT",
          headers: myHeaders,
          body: JSON.stringify(body),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Failed to update EHR status:", errorText);
        return;
      }

      // console.log("EHR Upload Status changed to 'idle'");
    } catch (err) {
      console.error("Error in changeEHRStatusToIdle:", err.message);
    }
  };

  const changeStatusToLocked = async () => {
    try {
      const status = "Locked";
      const body = { status };

      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");
      myHeaders.append("token", getSessionToken());

      // 1. Lock the note
      await fetch(`${process.env.REACT_APP_API_URL}/notes/status/${patient.patient_id}`, {
        method: "PUT",
        headers: myHeaders,
        body: JSON.stringify(body),
      });
    } catch (err) {
      console.error(err.message);
    }
  };

  /**
   * Track when a patient note is finalized (locked).
   * @param {string} finalizeMethod - The method used to finalize (e.g., "athena", "4d")
   * @param {string} [patientId] - Optional patient ID override (defaults to patient.patient_id)
   */
  const trackNoteFinalized = (finalizeMethod, patientId = patient.patient_id) => {
    trackEvent(
      Events.PATIENT_NOTE_FINALIZED,
      {
        patient_id: patientId,
        finalize_method: finalizeMethod,
        org,
      },
      { groups: { patient: patientId } }
    );
  };

  const changeStatusToReview = async () => {
    try {
      const status = "Review";
      //// console.log(status);
      const body = { status };

      const myHeaders = new Headers();

      myHeaders.append("Content-Type", "application/json");
      myHeaders.append("token", getSessionToken());

      await fetch(`${process.env.REACT_APP_API_URL}/notes/status/${patient.patient_id}`, {
        method: "PUT",
        headers: myHeaders,
        body: JSON.stringify(body),
      });
      //// console.log("Status changed");
    } catch (err) {
      console.error(err.message);
    }
  };

  // Function to send log when user clicked 'Review Note'
  function log_review_note(patient_id) {
    sendServerLogging({
      body: {
        ...userJSON(localStorage.getItem("userName")),
        event: "REVIEW_NOTE",
        eventData: { mrn: patient.mrn },
        patientId: patient_id,
      },
    });
  }

  // function to send log and metrics when user clicks icd10 code button
  function code_click(patient_id) {
    sendServerLogging({
      body: {
        ...userJSON(localStorage.getItem("userName")),
        event: "ICD_10_CODE_CLICK",
        eventData: { mrn: patient.mrn },
        patientId: patient_id,
      },
    });
  }

  // function to send logs and metrics for when user is editing/modifying icd-10 codes.
  function modifying_codes_click(patient_id) {
    sendServerLogging({
      body: {
        ...userJSON(localStorage.getItem("userName")),
        event: "MODIFIED_ICD_10_CODE",
        eventData: { mrn: patient.mrn },
        patientId: patient_id,
      },
    });
  }

  const hasActiveTextSelection = () => {
    if (typeof window === "undefined") {
      return false;
    }
    const selection = window.getSelection();
    return Boolean(selection && selection.toString().trim().length);
  };

  const handleIcd10CodeClick = (code) => {
    if (hasActiveTextSelection()) {
      return;
    }
    setCurrentSelectedCode(code);
    code_click(patient.patient_id);
    if (isUnspecifiedAnemia(code.name)) {
      setShowAlternativeCodes(true);
    } else {
      checkSimilarOrAlternative(code);
    }
    modifying_codes_click(patient.patient_id);
  };

  function setCopyStatus(type) {
    if (copyStatusTimerRef.current) {
      clearTimeout(copyStatusTimerRef.current);
    }

    if (type === "note") {
      setIsCopied(true);
      setCopiedSummary(false);
      setIsTranscriptCopied(false);
    } else if (type === "summary") {
      setCopiedSummary(true);
      setIsCopied(false);
      setIsTranscriptCopied(false);
    } else if (type === "transcript") {
      setIsTranscriptCopied(true);
      setIsCopied(false);
      setCopiedSummary(false);
    }

    copyStatusTimerRef.current = setTimeout(() => {
      setIsCopied(false);
      setCopiedSummary(false);
      setIsTranscriptCopied(false);
    }, 4000);
  }

  async function onCopy(patient_id) {
    setCopyStatus("note");
    announceCopySuccess("Note copied to clipboard.");
    trackButtonCopy("note");

    sendServerLogging({
      body: {
        ...userJSON(localStorage.getItem("userName")),
        event: "NOTE_COPIED",
        eventData: { mrn: patient.mrn },
        patientId: patient_id,
      },
    });
  }

  async function onCopyTranscript(patient_id) {
    setCopyStatus("transcript");
    announceCopySuccess("Transcript copied to clipboard.");
    trackButtonCopy("transcript");

    sendServerLogging({
      body: {
        ...userJSON(localStorage.getItem("userName")),
        event: "TRANSCRIPT_COPIED",
        eventData: { mrn: patient.mrn },
        patientId: patient_id,
      },
    });
  }

  const formatICD10Codes = (icd10Data) => {
    if (!icd10Data) return "";

    // Check if selected_codes is a string. If so, parse it.
    let selectedCodesInit;
    if (typeof icd10Data.selected_codes === "string") {
      try {
        selectedCodesInit = JSON.parse(icd10Data.selected_codes);
      } catch (err) {
        console.error("Failed to parse selected_codes JSON:", err);
        return { icd10Data };
      }
    } else {
      selectedCodesInit = icd10Data.selected_codes;
    }

    // Ensure codes is an array
    if (!selectedCodesInit || !Array.isArray(selectedCodesInit.codes)) {
      return { icd10Data };
    }

    // Set historical codes if they exist
    if (icd10Data.historical) {
      // Create a flat array of all historical codes
      const flatHistoricalCodes = [
        ...(icd10Data.historical.primary || []),
        ...(icd10Data.historical.secondary || []),
        ...(icd10Data.historical.active || []),
      ];
      setHistoricalCodes(flatHistoricalCodes);
      // Filter out historical codes from the codes array
      selectedCodesInit.codes = selectedCodesInit.codes.filter(
        (code) => !flatHistoricalCodes.includes(code.code)
      );
    }

    // console.log("selectedCodesInit", selectedCodesInit);
    return selectedCodesInit;
  };

  const [description, setDescription] = useState(patient.description || "");

  // Section highlighting state
  const [selectedSectionWords, setSelectedSectionWords] = useState([]);
  const [highlightedTranscript, setHighlightedTranscript] = useState("");

  // Helper function to extract section content from note
  const extractSectionContent = (noteText, sectionName) => {
    if (!noteText) return "";

    const sectionRegex = new RegExp(
      `${sectionName}:\\s*([\\s\\S]*?)(?=\\n\\s*(?:ASSESSMENT|PLAN|SUBJECTIVE|OBJECTIVE):|$)`,
      "i"
    );
    const match = noteText.match(sectionRegex);

    if (match && match[1]) {
      return match[1].trim();
    }
    return "";
  };

  // Medical abbreviations dictionary
  const medicalAbbreviations = {
    dm: "diabetes mellitus",
    htn: "hypertension",
    cad: "coronary artery disease",
    copd: "chronic obstructive pulmonary disease",
    chf: "congestive heart failure",
    mi: "myocardial infarction",
    cvd: "cardiovascular disease",
    sob: "shortness of breath",
    doe: "dyspnea on exertion",
    cp: "chest pain",
    bp: "blood pressure",
    hr: "heart rate",
    rr: "respiratory rate",
    temp: "temperature",
    wt: "weight",
    ht: "height",
    bmi: "body mass index",
    a1c: "hemoglobin a1c",
    hgb: "hemoglobin",
    hct: "hematocrit",
    wbc: "white blood cell",
    rbc: "red blood cell",
    plt: "platelet",
    bun: "blood urea nitrogen",
    cr: "creatinine",
    gfr: "glomerular filtration rate",
    ldl: "low density lipoprotein",
    hdl: "high density lipoprotein",
    tg: "triglycerides",
    tsh: "thyroid stimulating hormone",
    t4: "thyroxine",
    pt: "prothrombin time",
    ptt: "partial thromboplastin time",
    inr: "international normalized ratio",
    ekg: "electrocardiogram",
    ecg: "electrocardiogram",
    cxr: "chest x-ray",
    ct: "computed tomography",
    mri: "magnetic resonance imaging",
    us: "ultrasound",
    pe: "physical examination",
    hpi: "history of present illness",
    pmh: "past medical history",
    psh: "past surgical history",
    fh: "family history",
    sh: "social history",
    ros: "review of systems",
    nkda: "no known drug allergies",
    nka: "no known allergies",
    prn: "as needed",
    bid: "twice daily",
    tid: "three times daily",
    qid: "four times daily",
    qd: "once daily",
    qhs: "at bedtime",
    ac: "before meals",
    pc: "after meals",
    po: "by mouth",
    iv: "intravenous",
    im: "intramuscular",
    sq: "subcutaneous",
    sl: "sublingual",
  };

  // Medical phrases dictionary (multi-word terms)
  const medicalPhrases = [
    "diabetes mellitus",
    "type 1 diabetes",
    "type 2 diabetes",
    "blood pressure",
    "heart rate",
    "chest pain",
    "shortness of breath",
    "myocardial infarction",
    "coronary artery disease",
    "congestive heart failure",
    "chronic obstructive pulmonary disease",
    "acute myocardial infarction",
    "atrial fibrillation",
    "ventricular tachycardia",
    "pulmonary embolism",
    "deep vein thrombosis",
    "kidney disease",
    "liver disease",
    "thyroid disease",
    "brain fog",
    "blurry vision",
    "weight loss",
    "weight gain",
    "abdominal pain",
    "back pain",
    "joint pain",
    "muscle pain",
    "head pain",
    "neck pain",
    "leg pain",
    "arm pain",
    "chronic pain",
    "acute pain",
    "blood sugar",
    "blood glucose",
    "insulin resistance",
    "metabolic syndrome",
    "sleep apnea",
    "acid reflux",
    "gastroesophageal reflux",
    "irritable bowel",
    "inflammatory bowel",
    "urinary tract infection",
    "kidney stones",
    "gallbladder disease",
    "liver function",
    "kidney function",
    "heart function",
    "lung function",
    "thyroid function",
    "adrenal function",
  ];

  // Helper function to extract words and phrases from section content
  const extractWordsFromSection = (sectionContent) => {
    if (!sectionContent) return [];

    const cleanedContent = sectionContent
      .replace(/[\n\r]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();

    const matchedTerms = new Set();

    // 1. Extract medical phrases first (longer matches take priority)
    medicalPhrases.forEach((phrase) => {
      if (cleanedContent.includes(phrase.toLowerCase())) {
        matchedTerms.add(phrase.toLowerCase());
        // Also add individual words from the phrase
        phrase
          .toLowerCase()
          .split(" ")
          .forEach((word) => {
            if (word.length > 2) matchedTerms.add(word);
          });
      }
    });

    // 2. Expand abbreviations and add both abbreviation and expansion
    Object.entries(medicalAbbreviations).forEach(([abbrev, expansion]) => {
      const abbrevRegex = new RegExp(`\\b${abbrev}\\b`, "gi");
      if (abbrevRegex.test(cleanedContent)) {
        matchedTerms.add(abbrev.toLowerCase());
        matchedTerms.add(expansion.toLowerCase());
        // Add individual words from expansion
        expansion
          .toLowerCase()
          .split(" ")
          .forEach((word) => {
            if (word.length > 2) matchedTerms.add(word);
          });
      }
    });

    // 3. Extract individual meaningful words
    const words = cleanedContent
      .replace(/[^a-zA-Z0-9\s]/g, " ")
      .split(/\s+/)
      .filter((word) => word.length > 2)
      .filter(
        (word) =>
          !/^(the|and|or|of|to|in|for|with|by|from|up|on|at|as|is|was|are|were|be|been|have|has|had|do|does|did|will|would|could|should|may|might|can|must|shall|a|an|this|that|these|those|but|not|all|any|can|could|each|few|more|most|other|some|such|no|nor|too|very|will)$/i.test(
            word
          )
      );

    words.forEach((word) => matchedTerms.add(word.toLowerCase()));

    return Array.from(matchedTerms);
  };

  // Function to highlight words and phrases in transcript
  const highlightWordsInTranscript = (transcript, wordsToHighlight) => {
    if (!transcript || !wordsToHighlight.length) return transcript;

    let highlightedText = transcript;

    // Sort terms by length (longest first) to prioritize phrase matches over individual words
    const sortedTerms = wordsToHighlight
      .filter((term) => term && term.length > 2)
      .sort((a, b) => b.length - a.length);

    // Simple approach: highlight each term without complex overlap detection
    sortedTerms.forEach((term) => {
      // Escape special regex characters
      const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

      // Create regex for word boundaries
      const regex = new RegExp(`\\b${escapedTerm}\\b`, "gi");

      // Apply highlighting - avoid highlighting already highlighted content
      highlightedText = highlightedText.replace(regex, (match) => {
        // Don't highlight if this text is already inside a span
        return `<mark style="background-color: yellow; padding: 2px; border-radius: 3px;">${match}</mark>`;
      });
    });

    return highlightedText;
  };

  // Handle text selection in note textarea
  const handleNoteSelection = (e) => {
    const textarea = e.target;
    updateNoteCursorPosition(e);
    const selectedText = textarea.value.substring(textarea.selectionStart, textarea.selectionEnd);

    // Check if selected text contains any of the section headings
    const sectionHeadings = ["ASSESSMENT", "PLAN", "SUBJECTIVE", "OBJECTIVE"];
    const selectedSection = sectionHeadings.find((section) =>
      selectedText.toUpperCase().includes(section)
    );

    if (selectedSection) {
      // Extract content from the selected section
      const sectionContent = extractSectionContent(note, selectedSection);
      const wordsFromSection = extractWordsFromSection(sectionContent);

      // Update state with extracted words
      setSelectedSectionWords(wordsFromSection);

      // Generate highlighted transcript
      const highlighted = highlightWordsInTranscript(diarizedTranscript, wordsFromSection);
      setHighlightedTranscript(highlighted);

      // console.log(`Selected section: ${selectedSection}`);
      // console.log(`Extracted words:`, wordsFromSection);
    }
  };

  const updateNoteCursorPosition = (event) => {
    const nextPosition = event?.target?.selectionStart;
    if (typeof nextPosition === "number") {
      setCursorPosition(nextPosition);
      noteDictationSelectionRef.current = {
        start: nextPosition,
        end:
          typeof event?.target?.selectionEnd === "number"
            ? event.target.selectionEnd
            : nextPosition,
      };
    }
  };

  const updateSummaryDictationSelection = (event) => {
    const selectionStart = event?.target?.selectionStart;
    if (typeof selectionStart !== "number") return;

    noteDictationSelectionRef.current = {
      start: selectionStart,
      end:
        typeof event?.target?.selectionEnd === "number"
          ? event.target.selectionEnd
          : selectionStart,
    };
  };

  const syncEditorAfterProgrammaticEdit = ({
    target,
    previousValue,
    nextValue,
    selectionStart,
    selectionEnd,
  }) => {
    const isActiveDictationTarget = noteDictationTargetRef.current === target;
    const synchronized = syncNoteDictationAfterEdit({
      previousValue,
      nextValue,
      interimRange: isActiveDictationTarget ? noteDictationInterimRangeRef.current : null,
      selectionStart,
      selectionEnd,
    });

    if (isActiveDictationTarget) {
      noteDictationInterimRangeRef.current = synchronized.interimRange;
      if (synchronized.wasInterimRangeInvalidated) {
        stopEditorDictationSession();
      }
    }
    noteDictationSelectionRef.current = synchronized.selection;

    if (target === "summary") {
      patientSummaryRef.current = nextValue;
    } else {
      noteRef.current = nextValue;
    }
  };

  const applyEditorSelection = (textarea, selectionStart, selectionEnd = selectionStart) => {
    textarea.setSelectionRange(selectionStart, selectionEnd);
    noteDictationSelectionRef.current = {
      start: selectionStart,
      end: selectionEnd,
    };
  };

  // Function to clear highlighting
  const clearHighlighting = () => {
    setSelectedSectionWords([]);
    setHighlightedTranscript("");
  };
  const [note, setNote] = useState("");
  // Mirror the latest note into a ref so async flows (e.g. vitals refresh) read the current value after awaiting, instead of a stale closure snapshot that could clobber edits made while the request was in flight
  const noteRef = useRef(note);
  useEffect(() => {
    noteRef.current = note;
  }, [note]);
  const [allowTabNavigation, setAllowTabNavigation] = useState(false);
  const [showVitalsOutdatedNotice, setShowVitalsOutdatedNotice] = useState(false);
  const [isRefreshingVitals, setIsRefreshingVitals] = useState(false);
  const [isVitalsFallbackExpanded, setIsVitalsFallbackExpanded] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [isTranscriptCopied, setIsTranscriptCopied] = useState(false);
  const [codingInfo, setCodingInfo] = useState("");
  const [LOSCode, setLOSCode] = useState("");
  const [encounterTime, setEncounterTime] = useState("");
  const [timeBasedCode, setTimeBasedCode] = useState("");
  const [icd_10_codes, setICD_10_codes] = useState("");
  const [orders, setOrders] = useState("");
  const [ordersV2, setOrdersV2] = useState({});
  const [patientData, setPatientData] = useState({});
  const [selectedCodes, setSelectedCodes] = useState({});
  const [primaryCodes, setPrimaryCodes] = useState([]);
  const [secondaryCodes, setSecondaryCodes] = useState([]);
  reviewUnsavedGuardRef.current = {
    status,
    note,
    savedNote: savedDbNote,
    isReadOnlyCancelled,
    timeSpentChanged,
    selectedCodesChanged: haveSelectedCodesChanged(
      selectedCodes,
      loadedSnapshotRef.current?.selectedCodes
    ),
  };

  //stores the original icd10 codes json with default and selected
  const [originalCodes, setOriginalCodes] = useState({});

  //state variable to keep track of if alternative or similar codes modal will show up.
  const [showAlternativeCodes, setShowAlternativeCodes] = useState(false);
  const uploadBlockedGate = useFeatureGate("is-upload-blocked");
  const isUploadBlocked = uploadBlockedGate.hasFeature("on");
  const vvcDotphraseGate = useFeatureGate("vvc-dotphrase");
  const hasVvcDotphraseAccess = vvcDotphraseGate.hasFeature("on");
  const sharedMacros = useSharedMacrosQuery();

  // Macros Logic:

  //macros state variables. initalizing with the structure of the json object to reduce number of null checks and make code cleaner.
  const [macros, setMacros] = useState({
    suggested_macros: [
      {
        dot_phrase: "",
        keyword: "",
        value: "",
      },
    ],
    accepted: [],
    added_macros: [],
  });

  //state var to handle macro hover events
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [cursorPosition, setCursorPosition] = useState(0);

  const tooltipRef = useRef(null);

  const textareaRef = useRef(null);
  const noteHighlightRef = useRef(null);
  const noteHighlightContentRef = useRef(null);
  const summaryTextareaRef = useRef(null);

  const { trackButtonCopy } = useCopyTracking({
    patientId: patient.patient_id,
    org,
    containerRef: textareaRef,
  });

  const [isNoteEditorActive, setIsNoteEditorActive] = useState(false);
  const [isSummaryEditorActive, setIsSummaryEditorActive] = useState(false);
  const [highlightSourceText, setHighlightSourceText] = useState(false);
  const [isHighlighting, setIsHighlighting] = useState(false);
  const [carryForwardHighlightRanges, setCarryForwardHighlightRanges] = useState([]);
  const hasHighlightSources = useMemo(
    () => hasCarryForwardHighlightSources(chartHistory),
    [chartHistory]
  );
  useEffect(() => {
    if (!hasHighlightSources && highlightSourceText) {
      setHighlightSourceText(false);
    }
  }, [hasHighlightSources, highlightSourceText]);
  const vvcPlaceholderRanges = useMemo(() => getVvcPlaceholderRanges(note), [note]);
  useEffect(() => {
    if (!highlightSourceText) {
      setCarryForwardHighlightRanges([]);
      setIsHighlighting(false);
      return undefined;
    }

    let cancelled = false;
    setIsHighlighting(true);

    const timeoutId = setTimeout(() => {
      if (cancelled) return;
      const ranges = buildCarryForwardHighlightRanges(note, chartHistory);
      if (cancelled) return;
      setCarryForwardHighlightRanges(ranges);
      setIsHighlighting(false);
    }, CARRY_FORWARD_HIGHLIGHT_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [highlightSourceText, note, chartHistory]);

  const noteHighlightRanges = useMemo(
    () => buildNoteHighlightRanges(vvcPlaceholderRanges, carryForwardHighlightRanges),
    [vvcPlaceholderRanges, carryForwardHighlightRanges]
  );
  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    const highlight = noteHighlightRef.current;
    const highlightContent = noteHighlightContentRef.current;

    if (!textarea || !highlight || !highlightContent) return undefined;

    const syncHighlightLayout = () => {
      highlight.style.width = `${textarea.clientWidth}px`;
      highlight.style.height = `${textarea.clientHeight}px`;
      highlightContent.style.transform = `translate(${-textarea.scrollLeft}px, ${-textarea.scrollTop}px)`;
    };

    syncHighlightLayout();

    const resizeObserver =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(syncHighlightLayout);
    resizeObserver?.observe(textarea);
    window.addEventListener("resize", syncHighlightLayout);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", syncHighlightLayout);
    };
  }, [noteHighlightRanges.length]);

  const selectVvcPlaceholderAtCursor = (textarea) => {
    if (textarea.selectionStart !== textarea.selectionEnd) {
      return;
    }

    const placeholder = vvcPlaceholderRanges.find(
      ({ start, end }) => textarea.selectionStart >= start && textarea.selectionStart <= end
    );

    if (!placeholder) return;

    requestAnimationFrame(() => {
      textarea.setSelectionRange(placeholder.start, placeholder.end);
      setCursorPosition(placeholder.start);
    });
  };

  const canRefreshVitalsFromSource = hasValidAppointmentId(patient?.appointment_id);
  const latestChartHistoryVitals = useMemo(
    () => getLatestVitalsFromChartHistory(chartHistory),
    [chartHistory]
  );
  const hasLatestChartHistoryVitals = latestChartHistoryVitals.trim().length > 0;

  //Feedback state variables
  const initialFeedback = patient.user_feedback || {};
  const [userFeedback, setUserFeedback] = useState(initialFeedback);
  const [rating, setRating] = useState(initialFeedback?.rating ?? null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackLiveMessage, setFeedbackLiveMessage] = useState("");
  const hasTemporaryRating = rating !== null && rating !== undefined;
  const currentFeedbackRating =
    showFeedbackModal && hasTemporaryRating ? rating : (userFeedback?.rating ?? 0);

  const handleNoteKeyDown = (event) => {
    if (event.key === "Escape") {
      const textarea = event.currentTarget;
      setCursorPosition(textarea.selectionStart || 0);
      event.preventDefault();
      event.stopPropagation();
      if (event.nativeEvent?.stopImmediatePropagation) {
        event.nativeEvent.stopImmediatePropagation();
      }
      setIsNoteEditorActive(false);
      textarea.blur();
      return;
    }

    if (!isNoteEditorActive) {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        setIsNoteEditorActive(true);
      }
      return;
    }

    if (event.key === "Tab" && event.shiftKey) {
      // Allow Shift+Tab to move focus out of the textarea.
      setCursorPosition(event.currentTarget.selectionStart || 0);
      return;
    }

    if (event.key !== "Tab") {
      return;
    }

    event.preventDefault();

    const textarea = event.currentTarget;
    const value = textarea.value;
    const selectionStart = textarea.selectionStart;
    const selectionEnd = textarea.selectionEnd;
    const indent = "  ";
    const hasSelection = selectionStart !== selectionEnd;

    if (event.shiftKey) {
      if (!hasSelection) {
        const lineStart = value.lastIndexOf("\n", selectionStart - 1) + 1;
        const linePrefix = value.slice(lineStart, lineStart + indent.length);

        if (linePrefix !== indent) {
          return;
        }

        const nextValue = value.slice(0, lineStart) + value.slice(lineStart + indent.length);
        const nextPosition = Math.max(lineStart, selectionStart - indent.length);

        syncEditorAfterProgrammaticEdit({
          target: "note",
          previousValue: value,
          nextValue,
          selectionStart: nextPosition,
          selectionEnd: nextPosition,
        });
        setNote(nextValue);
        setIsCopied(false);
        requestAnimationFrame(() => {
          applyEditorSelection(textarea, nextPosition);
          setCursorPosition(nextPosition);
        });
        return;
      }

      const blockStart = value.lastIndexOf("\n", selectionStart - 1) + 1;
      const selectedBlock = value.slice(blockStart, selectionEnd);
      const lines = selectedBlock.split("\n");
      let totalRemoved = 0;

      const outdentedLines = lines.map((line) => {
        if (line.startsWith(indent)) {
          totalRemoved += indent.length;
          return line.slice(indent.length);
        }
        return line;
      });

      const nextBlock = outdentedLines.join("\n");
      const nextValue = value.slice(0, blockStart) + nextBlock + value.slice(selectionEnd);
      const firstLineRemoved = lines[0].startsWith(indent) ? indent.length : 0;
      const nextStart = Math.max(blockStart, selectionStart - firstLineRemoved);
      const nextEnd = Math.max(nextStart, selectionEnd - totalRemoved);

      syncEditorAfterProgrammaticEdit({
        target: "note",
        previousValue: value,
        nextValue,
        selectionStart: nextStart,
        selectionEnd: nextEnd,
      });
      setNote(nextValue);
      setIsCopied(false);
      requestAnimationFrame(() => {
        applyEditorSelection(textarea, nextStart, nextEnd);
        setCursorPosition(nextEnd);
      });
      return;
    }

    if (!hasSelection) {
      const nextValue = value.slice(0, selectionStart) + indent + value.slice(selectionEnd);
      const nextPosition = selectionStart + indent.length;

      syncEditorAfterProgrammaticEdit({
        target: "note",
        previousValue: value,
        nextValue,
        selectionStart: nextPosition,
        selectionEnd: nextPosition,
      });
      setNote(nextValue);
      setIsCopied(false);
      requestAnimationFrame(() => {
        applyEditorSelection(textarea, nextPosition);
        setCursorPosition(nextPosition);
      });
      return;
    }

    const blockStart = value.lastIndexOf("\n", selectionStart - 1) + 1;
    const selectedBlock = value.slice(blockStart, selectionEnd);
    const lines = selectedBlock.split("\n");
    const indentedBlock = lines.map((line) => indent + line).join("\n");
    const nextValue = value.slice(0, blockStart) + indentedBlock + value.slice(selectionEnd);
    const nextStart = selectionStart + indent.length;
    const nextEnd = selectionEnd + indent.length * lines.length;

    syncEditorAfterProgrammaticEdit({
      target: "note",
      previousValue: value,
      nextValue,
      selectionStart: nextStart,
      selectionEnd: nextEnd,
    });
    setNote(nextValue);
    setIsCopied(false);
    requestAnimationFrame(() => {
      applyEditorSelection(textarea, nextStart, nextEnd);
      setCursorPosition(nextEnd);
    });
  };

  const handleSummaryKeyDown = (event) => {
    if (event.key === "Escape") {
      const textarea = event.currentTarget;
      event.preventDefault();
      event.stopPropagation();
      if (event.nativeEvent?.stopImmediatePropagation) {
        event.nativeEvent.stopImmediatePropagation();
      }
      setIsSummaryEditorActive(false);
      textarea.blur();
      return;
    }

    if (!isSummaryEditorActive) {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        setIsSummaryEditorActive(true);
      }
      return;
    }

    if (event.key === "Tab" && event.shiftKey) {
      // Allow Shift+Tab to move focus out of the textarea.
      return;
    }

    if (event.key !== "Tab") {
      return;
    }

    event.preventDefault();

    const textarea = event.currentTarget;
    const value = textarea.value;
    const selectionStart = textarea.selectionStart;
    const selectionEnd = textarea.selectionEnd;
    const indent = "  ";
    const hasSelection = selectionStart !== selectionEnd;

    if (event.shiftKey) {
      if (!hasSelection) {
        const lineStart = value.lastIndexOf("\n", selectionStart - 1) + 1;
        const linePrefix = value.slice(lineStart, lineStart + indent.length);

        if (linePrefix !== indent) {
          return;
        }

        const nextValue = value.slice(0, lineStart) + value.slice(lineStart + indent.length);
        const nextPosition = Math.max(lineStart, selectionStart - indent.length);

        syncEditorAfterProgrammaticEdit({
          target: "summary",
          previousValue: value,
          nextValue,
          selectionStart: nextPosition,
          selectionEnd: nextPosition,
        });
        setPatientSummary(nextValue);
        setCopiedSummary(false);

        requestAnimationFrame(() => {
          applyEditorSelection(textarea, nextPosition);
        });
        return;
      }

      const blockStart = value.lastIndexOf("\n", selectionStart - 1) + 1;
      const selectedBlock = value.slice(blockStart, selectionEnd);
      const lines = selectedBlock.split("\n");
      let totalRemoved = 0;

      const outdentedLines = lines.map((line) => {
        if (line.startsWith(indent)) {
          totalRemoved += indent.length;
          return line.slice(indent.length);
        }
        return line;
      });

      const nextBlock = outdentedLines.join("\n");
      const nextValue = value.slice(0, blockStart) + nextBlock + value.slice(selectionEnd);
      const firstLineRemoved = lines[0].startsWith(indent) ? indent.length : 0;
      const nextStart = Math.max(blockStart, selectionStart - firstLineRemoved);
      const nextEnd = Math.max(nextStart, selectionEnd - totalRemoved);

      syncEditorAfterProgrammaticEdit({
        target: "summary",
        previousValue: value,
        nextValue,
        selectionStart: nextStart,
        selectionEnd: nextEnd,
      });
      setPatientSummary(nextValue);
      setCopiedSummary(false);

      requestAnimationFrame(() => {
        applyEditorSelection(textarea, nextStart, nextEnd);
      });
      return;
    }

    if (!hasSelection) {
      const nextValue = value.slice(0, selectionStart) + indent + value.slice(selectionEnd);
      const nextPosition = selectionStart + indent.length;

      syncEditorAfterProgrammaticEdit({
        target: "summary",
        previousValue: value,
        nextValue,
        selectionStart: nextPosition,
        selectionEnd: nextPosition,
      });
      setPatientSummary(nextValue);
      setCopiedSummary(false);

      requestAnimationFrame(() => {
        applyEditorSelection(textarea, nextPosition);
      });
      return;
    }

    const blockStart = value.lastIndexOf("\n", selectionStart - 1) + 1;
    const selectedBlock = value.slice(blockStart, selectionEnd);
    const lines = selectedBlock.split("\n");
    const indentedBlock = lines.map((line) => indent + line).join("\n");
    const nextValue = value.slice(0, blockStart) + indentedBlock + value.slice(selectionEnd);
    const nextStart = selectionStart + indent.length;
    const nextEnd = selectionEnd + indent.length * lines.length;

    syncEditorAfterProgrammaticEdit({
      target: "summary",
      previousValue: value,
      nextValue,
      selectionStart: nextStart,
      selectionEnd: nextEnd,
    });
    setPatientSummary(nextValue);
    setCopiedSummary(false);

    requestAnimationFrame(() => {
      applyEditorSelection(textarea, nextStart, nextEnd);
    });
  };

  const persistUserFeedback = useCallback(
    async (feedback) => {
      if (!patient?.patient_id) return;

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/notes/todos/${patient.patient_id}/user-feedback`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              token: getSessionToken(),
            },
            body: JSON.stringify({ userFeedback: feedback ?? null }),
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to save feedback: ${response.statusText}`);
        }
      } catch (err) {
        console.error("Failed to save feedback:", err);
      }
    },
    [patient?.patient_id]
  );

  const handleRatingChange = useCallback(
    (star) => {
      setRating(star);
      setFeedbackLiveMessage(`Feedback rating selected: ${star} star${star > 1 ? "s" : ""}.`);
      setUserFeedback((prevFeedback) => {
        const nextFeedback = {
          ...(prevFeedback || {}),
          rating: star,
        };
        persistUserFeedback(nextFeedback);
        return nextFeedback;
      });
    },
    [persistUserFeedback]
  );

  const handleFeedbackStarClick = (star) => {
    handleRatingChange(star);
    setFeedbackLiveMessage(
      `Feedback rating selected: ${star} star${star > 1 ? "s" : ""}. Feedback dialog opened.`
    );
    setShowFeedbackModal(true);
  };

  const handleFeedbackSubmit = useCallback(
    (feedback) => {
      const normalizedFeedback = feedback || {};
      setUserFeedback(normalizedFeedback);
      setRating(normalizedFeedback?.rating ?? null);
      persistUserFeedback(normalizedFeedback);
    },
    [persistUserFeedback]
  );

  useEffect(() => {
    const feedback = patient.user_feedback || {};
    setUserFeedback(feedback);
    setRating(feedback?.rating ?? null);
  }, [patient.patient_id, patient.user_feedback]);

  useEffect(() => {
    if (!isEditing) return;
    const selectedRating = Number(currentFeedbackRating) || 0;
    setFeedbackLiveMessage(
      selectedRating > 0
        ? `Feedback rating selector ready. ${selectedRating} star${selectedRating > 1 ? "s" : ""} currently selected.`
        : "Feedback rating selector ready. No rating currently selected."
    );
  }, [isEditing, currentFeedbackRating, patient.patient_id]);

  useEffect(() => {
    const filteredCodes = selectedCodes?.codes || [];

    setPrimaryCodes(filteredCodes.filter((code) => code.codeType === "Primary") || []);
    setSecondaryCodes(filteredCodes.filter((code) => code.codeType === "Secondary") || []);
  }, [selectedCodes, superOrg]);

  //function to check if the primary code is iron deficiency anemia, unspecified
  const isUnspecifiedAnemia = (codeName) => {
    if (codeName === "Iron deficiency anemia, unspecified" || codeName === "Anemia, unspecified") {
      return true;
    } else {
      return false;
    }
  };

  const saveSelectedCodes = async (newSelectedCodes) => {
    try {
      // Notes without ICD-10 data (or still loading) have nothing to persist.
      if (
        !newSelectedCodes ||
        typeof newSelectedCodes !== "object" ||
        !Array.isArray(newSelectedCodes.codes) ||
        newSelectedCodes.codes.length === 0
      ) {
        return true;
      }

      // Skip the write if codes are unchanged since load; avoids clobbering codes the pipeline may have regenerated while the note was open
      if (isUnchangedFromSnapshot(newSelectedCodes, loadedSnapshotRef.current?.selectedCodes)) {
        return true;
      }

      // console.log("=== DEBUG: About to save selected codes ===");
      // console.log("newSelectedCodes:", newSelectedCodes);
      // console.log("JSON.stringify result:", JSON.stringify(newSelectedCodes));
      // console.log("Content length:", JSON.stringify(newSelectedCodes).length);

      // Log the exact request body being sent
      const requestBody = JSON.stringify(newSelectedCodes);
      // console.log("=== REQUEST BODY DEBUG ===");
      // console.log("Request body:", requestBody);
      // console.log("Request body length:", requestBody.length);
      // console.log("Request body type:", typeof requestBody);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/notes/todos/icd10/${patient.patient_id}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            token: getSessionToken(),
          },
          body: requestBody, // Use the logged variable
        }
      );

      if (!response.ok) {
        throw new Error(`Error saving selected codes: ${response.statusText}`);
      }

      if (loadedSnapshotRef.current) {
        loadedSnapshotRef.current.selectedCodes = JSON.parse(JSON.stringify(newSelectedCodes));
      }
      return true;
    } catch (error) {
      console.error("Failed to save selected codes:", error);
      return false;
    }
  };

  const [diarizedTranscript, setDiarizedTranscript] = useState(patient.diarized_transcript);
  const [diarizedTranscriptCsvUrl, setDiarizedTranscriptCsvUrl] = useState(
    patient.diarized_transcript_csv_url
  );

  const [citedNoteData, setCitedNoteData] = useState(patient.cited_note);

  const [hcc_codes, setHCC_Codes] = useState(patient.hcc_codes);
  const [cpt_codes, setCPT_codes] = useState(patient.cpt_codes);
  const [selectedNoteTitle, setSelectedNoteTitle] = useState(null);
  const [noteTitleRequiredError, setNoteTitleRequiredError] = useState(false);
  const noteTitleStorageKey = "selected_note_title";
  const normalizedFavoriteValues = useMemo(() => {
    if (!Array.isArray(noteTitlesFavorites)) {
      return [];
    }

    return Array.from(
      new Set(
        noteTitlesFavorites
          .map((value) => (typeof value === "string" ? value.trim() : ""))
          .filter(Boolean)
      )
    );
  }, [noteTitlesFavorites]);
  const safeNoteTitleOptionValues = Array.isArray(noteTitleOptionValues)
    ? noteTitleOptionValues
    : [];
  const safeNoteTitleValueSet =
    noteTitleOptionValueSet instanceof Set
      ? noteTitleOptionValueSet
      : new Set(safeNoteTitleOptionValues);
  const ienForSubmit = useMemo(() => {
    if (!isNoteTitlesEnabled) return null;
    if (!safeNoteTitleOptionValues.length) return null;
    return safeNoteTitleValueSet.has(selectedNoteTitle) ? selectedNoteTitle : null;
  }, [
    isNoteTitlesEnabled,
    safeNoteTitleOptionValues.length,
    safeNoteTitleValueSet,
    selectedNoteTitle,
  ]);
  const shouldShowNoteTitleDropdown =
    !isStandaloneUser && isNoteTitlesEnabled && safeNoteTitleOptionValues.length > 0;
  const requireNoteTitleSelection = shouldShowNoteTitleDropdown;
  const isNoteTitleSelectionMissing = requireNoteTitleSelection && !isMockPatient && !ienForSubmit;
  const noteTitleRequiredErrorId = `note-title-required-${patient.patient_id}`;
  const noteTitleSectionRef = useRef(null);

  const focusNoteTitleSection = useCallback(() => {
    requestAnimationFrame(() => {
      if (!noteTitleSectionRef.current) return;
      noteTitleSectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      const noteTitleSearchInput = noteTitleSectionRef.current.querySelector(
        'input[aria-label="Search note titles"]'
      );
      if (noteTitleSearchInput && typeof noteTitleSearchInput.focus === "function") {
        noteTitleSearchInput.focus();
      }
    });
  }, []);
  const ensureNoteTitleSelection = useCallback(() => {
    if (!isNoteTitleSelectionMissing) {
      return true;
    }

    setNoteTitleRequiredError(true);
    focusNoteTitleSection();
    return false;
  }, [focusNoteTitleSection, isNoteTitleSelectionMissing]);

  const isLocked = status === "Locked";

  const upsertNoteDictationText = useCallback((transcript, isFinal, { isSessionStart } = {}) => {
    const dictationTarget = noteDictationTargetRef.current;
    const isSummaryTarget = dictationTarget === "summary";
    const targetTextarea = isSummaryTarget ? summaryTextareaRef.current : textareaRef.current;
    const selection = noteDictationSelectionRef.current;
    const result = insertNoteDictationText({
      currentValue:
        targetTextarea?.value ?? (isSummaryTarget ? patientSummaryRef.current : noteRef.current),
      transcript,
      selectionStart: selection.start,
      selectionEnd: selection.end,
      previousInterimRange: noteDictationInterimRangeRef.current,
      isFinal,
      isSessionStart,
    });

    if (!result) return;

    noteDictationInterimRangeRef.current = result.interimRange;
    noteDictationSelectionRef.current = {
      start: result.cursorPosition,
      end: result.cursorPosition,
    };

    if (isSummaryTarget) {
      patientSummaryRef.current = result.value;
      setPatientSummary(result.value);
      setCopiedSummary(false);
      setIsSummaryEditorActive(true);
    } else {
      noteRef.current = result.value;
      setNote(result.value);
      setIsCopied(false);
      setCursorPosition(result.cursorPosition);
      setIsNoteEditorActive(true);
    }

    requestAnimationFrame(() => {
      if (noteDictationTargetRef.current !== dictationTarget || !targetTextarea) return;
      targetTextarea.focus();
      targetTextarea.setSelectionRange(result.cursorPosition, result.cursorPosition);
    });
  }, []);

  const editorDictation = useDeepgramDictation({
    purpose: "additional-context",
    sessionKey: patient.patient_id,
    onTranscript: upsertNoteDictationText,
    onListening: () => {
      if (noteDictationTargetRef.current === "summary") {
        setIsSummaryEditorActive(true);
        summaryTextareaRef.current?.focus();
      } else {
        setIsNoteEditorActive(true);
        textareaRef.current?.focus();
      }
    },
    onStopped: () => {
      noteDictationInterimRangeRef.current = null;
      noteDictationTargetRef.current = null;
      setActiveNoteDictationTarget(null);
    },
    onError: (message) => toast.error(message),
    messages: {
      unsupportedMimeType: "This browser does not support microphone dictation for notes.",
      connectionFailed: "Note dictation connection failed. Please try again.",
      startFailed: "Unable to start note dictation. Check your browser settings.",
    },
    logLabel: "Note dictation",
  });
  const {
    isDictating: isNoteDictating,
    isStarting: isNoteDictationStarting,
    status: noteDictationStatus,
    stop: stopEditorDictationSession,
    toggle: toggleEditorDictation,
  } = editorDictation;

  const stopNoteDictation = useCallback(
    ({ suppressStoppedToast = false, clearStatus = false } = {}) => {
      stopEditorDictationSession({ clearStatus });
      if (!suppressStoppedToast) toast.info("Note dictation stopped.");
    },
    [stopEditorDictationSession]
  );

  const handleToggleNoteDictation = useCallback(
    async (dictationTarget = "note") => {
      if (isNoteDictationStarting) {
        return;
      }

      if (isNoteDictating) {
        stopNoteDictation();
        return;
      }

      if (isLocked) {
        return;
      }

      if (smartAssistDictationState.isDictating || smartAssistDictationState.isStarting) {
        toast.error("Stop Smart Assist dictation before dictating into the note.");
        return;
      }

      noteDictationInterimRangeRef.current = null;
      noteDictationTargetRef.current = dictationTarget;
      setActiveNoteDictationTarget(dictationTarget);
      await toggleEditorDictation();
    },
    [
      isLocked,
      isNoteDictating,
      isNoteDictationStarting,
      smartAssistDictationState,
      stopNoteDictation,
      toggleEditorDictation,
    ]
  );

  useEffect(() => {
    if (!activeNoteDictationTarget || (!isNoteDictating && !isNoteDictationStarting)) {
      return;
    }

    const targetTab = activeNoteDictationTarget === "summary" ? "summary" : "home";
    if (effectiveNavTab !== targetTab) {
      stopNoteDictation({ suppressStoppedToast: true, clearStatus: true });
    }
  }, [
    activeNoteDictationTarget,
    effectiveNavTab,
    isNoteDictating,
    isNoteDictationStarting,
    stopNoteDictation,
  ]);

  //useEffect to handle csv download
  useEffect(() => {
    setDiarizedTranscriptCsvUrl(patient.diarized_transcript_csv_url);
  }, [patient.diarized_transcript_csv_url]);

  //useEffect to handle cited note data updates
  useEffect(() => {
    setCitedNoteData(patient.cited_note);
  }, [patient.cited_note]);

  useEffect(() => {
    if (!isNoteTitlesEnabled) {
      setSelectedNoteTitle(null);
      return;
    }

    const storedValue = localStorage.getItem(noteTitleStorageKey);
    const fallbackFavoriteValue = normalizedFavoriteValues.find((favoriteValue) =>
      safeNoteTitleValueSet.has(favoriteValue)
    );

    if (storedValue && safeNoteTitleValueSet.has(storedValue)) {
      setSelectedNoteTitle(storedValue);
      return;
    }

    if (fallbackFavoriteValue) {
      setSelectedNoteTitle((prev) => {
        if (prev && safeNoteTitleValueSet.has(prev)) return prev;
        return fallbackFavoriteValue;
      });
      return;
    }

    setSelectedNoteTitle((prev) => {
      if (prev && safeNoteTitleValueSet.has(prev)) return prev;
      return null;
    });
  }, [isNoteTitlesEnabled, normalizedFavoriteValues, noteTitleStorageKey, safeNoteTitleValueSet]);

  useEffect(() => {
    if (!isNoteTitlesEnabled || !selectedNoteTitle) {
      return;
    }
    localStorage.setItem(noteTitleStorageKey, selectedNoteTitle);
  }, [isNoteTitlesEnabled, selectedNoteTitle, noteTitleStorageKey]);

  useEffect(() => {
    if (ienForSubmit) {
      setNoteTitleRequiredError(false);
    }
  }, [ienForSubmit]);

  const handleNoteTitleChange = useCallback(
    (event) => {
      try {
        const nextValue = event.target.value;
        const trimmedNextValue =
          typeof nextValue === "string" ? nextValue.trim() : String(nextValue || "").trim();
        const previousValue =
          typeof selectedNoteTitle === "string"
            ? selectedNoteTitle.trim()
            : String(selectedNoteTitle || "").trim();
        const selectedEntry = Array.isArray(noteTitlesEntries)
          ? noteTitlesEntries.find((entry) => (entry?.ien || entry?.title) === trimmedNextValue)
          : null;

        setSelectedNoteTitle(nextValue);
        setNoteTitleRequiredError(false);
        if (nextValue) {
          localStorage.setItem(noteTitleStorageKey, nextValue);
        }

        if (trimmedNextValue && trimmedNextValue !== previousValue) {
          trackEvent(
            Events.NOTE_TITLE_SELECTED,
            {
              patient_id: patient.patient_id,
              note_title_value: trimmedNextValue,
              note_title_label: selectedEntry?.title || trimmedNextValue,
            },
            { groups: { patient: patient.patient_id } }
          );
        }
      } catch (error) {
        Sentry.captureException(error, {
          tags: { flow: "noteTitleSelection" },
          extra: {
            patientId: patient.patient_id,
            selectedNoteTitle: event?.target?.value || null,
          },
        });
      }
    },
    [noteTitleStorageKey, noteTitlesEntries, patient.patient_id, selectedNoteTitle]
  );

  const handleNoteTitleFavoriteToggle = useCallback(
    async (entry) => {
      if (!isNoteTitlesEnabled || typeof setNoteTitlesFavorites !== "function") {
        return;
      }

      const favoriteValue =
        typeof entry?.ien === "string" && entry.ien.trim()
          ? entry.ien.trim()
          : typeof entry?.title === "string"
            ? entry.title.trim()
            : "";
      const sta3n = localStorage.getItem("va_sta3n");
      const requestUserId = String(userId || patient?.user_id || "");

      if (!favoriteValue || !sta3n || !requestUserId) {
        toast.error("Unable to update note title favorites right now.");
        return;
      }

      const shouldRemove = normalizedFavoriteValues.includes(favoriteValue);
      const action = shouldRemove ? "remove" : "add";
      const applyFavoriteAction = (favorites, favoriteToApply, actionToApply) => {
        const normalizedFavorites = Array.isArray(favorites)
          ? Array.from(
              new Set(
                favorites
                  .map((favorite) => (typeof favorite === "string" ? favorite.trim() : ""))
                  .filter(Boolean)
              )
            )
          : [];
        const nextFavoritesSet = new Set(normalizedFavorites);
        if (actionToApply === "remove") {
          nextFavoritesSet.delete(favoriteToApply);
        } else {
          nextFavoritesSet.add(favoriteToApply);
        }
        return Array.from(nextFavoritesSet);
      };

      // Apply immediately so local state never gets replaced by partial API arrays.
      setNoteTitlesFavorites((previousFavorites) =>
        applyFavoriteAction(previousFavorites, favoriteValue, action)
      );
      try {
        await noteTitleFavoritesMutation.mutateAsync({
          userId: requestUserId,
          sta3n,
          favorite: favoriteValue,
          action,
        });
      } catch (error) {
        // Revert only this toggle on failure without clobbering other favorites.
        setNoteTitlesFavorites((previousFavorites) =>
          applyFavoriteAction(
            previousFavorites,
            favoriteValue,
            action === "remove" ? "add" : "remove"
          )
        );
        Sentry.captureException(error, {
          tags: { flow: "noteTitleFavoriteToggle" },
          extra: {
            patientId: patient?.patient_id,
            favoriteValue,
            action,
          },
        });
        toast.error(error?.message || "Failed to update note title favorites.");
      }
    },
    [
      isNoteTitlesEnabled,
      normalizedFavoriteValues,
      noteTitleFavoritesMutation,
      patient?.user_id,
      setNoteTitlesFavorites,
      userId,
    ]
  );

  useEffect(() => {
    const latestVitals = latestChartHistoryVitals;
    const hasVitalsField = hasVitalsFieldInChartHistory(chartHistory);
    const selectedScheduleDate = getSelectedScheduleReferenceDate();
    const vitalsDatedToday = hasTodayDateInVitals(latestVitals, selectedScheduleDate);
    const currentNoteHasReferenceVitals = noteHasReferenceDateInVitals(note, selectedScheduleDate);
    const savedNoteHasReferenceVitals = noteHasReferenceDateInVitals(
      savedDbNote,
      selectedScheduleDate
    );
    const shouldShowRefreshVitalsCta =
      !isLocked &&
      !isStandaloneUser &&
      canRefreshVitalsFromSource &&
      (!hasVitalsField ||
        !vitalsDatedToday ||
        !currentNoteHasReferenceVitals ||
        !savedNoteHasReferenceVitals);
    setShowVitalsOutdatedNotice(shouldShowRefreshVitalsCta);
  }, [
    chartHistory,
    latestChartHistoryVitals,
    note,
    savedDbNote,
    isLocked,
    isStandaloneUser,
    canRefreshVitalsFromSource,
  ]);

  useEffect(() => {
    if (!showVitalsOutdatedNotice || !hasLatestChartHistoryVitals) {
      setIsVitalsFallbackExpanded(false);
    }
  }, [showVitalsOutdatedNotice, hasLatestChartHistoryVitals]);

  const handleCopyCurrentVitals = useCallback(async () => {
    if (!hasLatestChartHistoryVitals) {
      toast.info("No current vitals available to copy.");
      return;
    }

    try {
      await navigator.clipboard.writeText(latestChartHistoryVitals);
      toast.success("Current vitals copied.");
    } catch (error) {
      console.error("Failed to copy current vitals:", error);
      toast.error("Unable to copy current vitals.");
    }
  }, [hasLatestChartHistoryVitals, latestChartHistoryVitals]);

  const handleRefreshVitals = useCallback(async () => {
    if (isLocked || !patient?.patient_id || !canRefreshVitalsFromSource) {
      return;
    }
    if (isCrossSiteCare) {
      toast.warning(crossSiteRelaunchMessage);
      return;
    }
    if (isStandaloneUser) {
      toast.info("Patient enrichment is disabled for standalone users.");
      return;
    }

    setIsRefreshingVitals(true);

    try {
      const baseUrl = localStorage.getItem("va_fhir_server");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/notes/todos/${patient.patient_id}/refresh-chapi`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token: getSessionToken(),
          },
          body: JSON.stringify({ base_url: baseUrl, scope: "vitals" }),
        }
      );

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        const message = errorBody?.message || "Failed to refresh vitals.";
        throw new Error(message);
      }

      const data = await response.json().catch(() => ({}));
      const latestChartHistoryRaw = data?.chartHistory ?? data?.chart_history ?? {};
      const latestChartHistory = parseChartHistorySafe(latestChartHistoryRaw);
      const latestVitals = getLatestVitalsFromChartHistory(latestChartHistory);
      const selectedScheduleDate = getSelectedScheduleReferenceDate();
      const vitalsDatedToday = hasTodayDateInVitals(latestVitals, selectedScheduleDate);

      if (!latestVitals) {
        // Keep the refresh call-to-action visible for intentional empty-state vitals,
        // so providers can retry later without losing access to refresh.
        setShowVitalsOutdatedNotice(true);
        toast.error("No vitals available to refresh.");
        return;
      }

      // CHAPI may return stale vitals; never rewrite note vitals unless they are from today.
      if (!vitalsDatedToday) {
        setChartHistory(latestChartHistory);
        setShowVitalsOutdatedNotice(true);
        toast.info("Latest vitals are not from today. Note was not updated.");
        return;
      }

      setChartHistory(latestChartHistory);
      // Read the current note from the ref (not a stale closure) so edits made while CHAPI was loading are not overwritten by the refresh
      const updatedNote = replaceVitalsSection(noteRef.current, latestVitals);
      // Persist the spliced vitals so the corrected note survives a reload and is what gets uploaded to the EHR; without this the refresh only updated local state and the note was never re-saved (root cause of the reported bug)
      const didSaveNote = await saveNoteWithOverride(updatedNote);
      if (!didSaveNote) {
        // Only claim success once the note is actually persisted; a failed save (e.g. note-title validation) must surface as an error, not a false "Vitals Updated Successfully" while the change stays local-only
        throw new Error("Vitals were refreshed but the updated note could not be saved.");
      }
      setIsCopied(false);
      setShowVitalsOutdatedNotice(true);
      toast.success(data?.refresh?.message || "Vitals Updated Successfully");
    } catch (error) {
      console.error("Failed to refresh vitals:", error);
      toast.error(error?.message || "Failed to refresh vitals.");
    } finally {
      setIsRefreshingVitals(false);
    }
  }, [
    isLocked,
    patient?.patient_id,
    isStandaloneUser,
    canRefreshVitalsFromSource,
    saveNoteWithOverride,
    isCrossSiteCare,
    crossSiteRelaunchMessage,
  ]);

  const [codesCount, setCodesCount] = useState(0);
  const [ordersCount, setOrdersCount] = useState(0);

  //useEffect to handle csv download
  useEffect(() => {
    setCodesCount(icd_10_codes?.match(/([A-Z]\d+\.\d+)/g)?.length);
  }, [icd_10_codes]);

  useEffect(() => {
    // Calculate total count from orders_v2 JSON
    let totalCount = 0;
    if (ordersV2 && typeof ordersV2 === "object") {
      totalCount =
        (ordersV2.tests?.length || 0) +
        (ordersV2.drugs?.length || 0) +
        (ordersV2.radiology?.length || 0);
    }
    setOrdersCount(totalCount);
  }, [ordersV2]);

  //delete modal state variables
  const [showDelete, setShowDelete] = useState(false);

  // Track which code we're deleting
  const [codeToDelete, setCodeToDelete] = useState(null);

  // EditPatient.jsx (snippet)
  const handleDeleteCode = (codeObject) => {
    if (!codeObject) return;
    // Remove from local state, but do NOT call saveSelectedCodes here
    const updatedCodes = selectedCodes.codes.filter((c) => c.code !== codeObject.code);
    setSelectedCodes({ ...selectedCodes, codes: updatedCodes });
    setShowDelete(false);
  };

  //function to determine if suggested codes or alternative modals show up
  const checkSimilarOrAlternative = (code) => {
    if (code.alternatives && Array.isArray(code.alternatives) && code.alternatives.length > 0) {
      setShowAlternativeCodes(true);
    } else if (code.similar) {
      setShowSuggestions(true);
    } else {
      setShowAlternativeCodes(false);
      setShowSuggestions(false);
    }
  };

  const [isFetching, setIsFetching] = useState(false);
  const [isNoteLoaded, setIsNoteLoaded] = useState(false);
  const [isDashboardDataFetched, setIsDashboardDataFetched] = useState(false);
  const isSaveUploadButtonDisabled =
    isCrossSiteCare ||
    isFetching ||
    submittingPatientId === patient.patient_id ||
    isNoteTitleSelectionMissing;
  const patientChatCapabilitiesQuery = usePatientChatCapabilities({
    patientId: patient?.patient_id,
    enabled: Boolean(patient?.patient_id),
  });
  const isPatientSmartAssistOpen =
    isPatientModalOpen &&
    patientChatCapabilitiesQuery.data?.enabled &&
    isPatientChatSidecarExpanded;
  useEffect(() => {
    if (!isPatientSmartAssistOpen) {
      return undefined;
    }

    document.body.classList.add("smart-assist-open");

    return () => {
      document.body.classList.remove("smart-assist-open");
    };
  }, [isPatientSmartAssistOpen]);
  const smartAssistDraftSaver = usePatientChatDraftSaver();
  // Matches the library the note editor's dot phrases expand from, including this
  // patient's suggested and selected macros, plus the specialty's shared prompts.
  // Shared prompts come first so a provider's own phrase with the same name wins.
  const smartAssistMacrosByDotPhrase = useMemo(
    () =>
      buildDotPhraseMap(
        sharedMacros,
        mergeTelehealthAvailableMacros(userMacros, hasVvcDotphraseAccess),
        macros?.added_macros,
        macros?.accepted,
        macros?.suggested_macros
      ),
    [sharedMacros, hasVvcDotphraseAccess, macros, userMacros]
  );

  const handleUseSmartAssistReplyAsNote = useCallback((reply) => {
    setNote(reply);
    setIsCopied(false);
    setIsNoteEditorActive(true);
    toast.success("Smart Assist rewrite applied to the note editor.");
  }, []);

  const handleResetNoteToSavedVersion = () => {
    setNote(savedDbNote || "");
    setIsCopied(false);
    setIsNoteEditorActive(false);
    toast.success("Note reset to initial saved version.");
  };

  useEffect(() => {
    const modalElement = patientModalRef.current;

    if (!modalElement) {
      return undefined;
    }

    const resetNoteCloseGuardState = () => {
      noteCloseConfirmedRef.current = false;
      pendingReviewCloseRef.current = null;
      setUnsavedCloseConfirmOpen(false);
    };
    const handleModalShow = () => {
      // Set before shown.bs.modal so React re-renders during open (e.g. setIsEditing
      // from the Review click) keep the `show` class instead of wiping Bootstrap's.
      isPatientModalOpenRef.current = true;
      document.body.classList.add("edit-patient-modal-open");
      setIsPatientModalOpen(true);
    };
    const handleModalShown = () => {
      resetNoteCloseGuardState();
      // Bootstrap sets aria-modal on show, which would hide the sidecar outside
      // this dialog from screen readers.
      if (isSidecarRenderedRef.current) {
        modalElement.removeAttribute("aria-modal");
      }
      // data-bs-focus="false" skips Bootstrap's focus entry. The sidecar precedes the
      // modal in the DOM, so without this the first Tab lands in Smart Assist.
      // Focus stays on the patient name: moving it again once the note loads
      // cuts off the screen reader mid-name, and JAWS doesn't repeat the dialog name.
      (modalTitleRef.current ?? modalElement).focus({ preventScroll: true });
      if (!isPatientChatSplitView) {
        setIsPatientChatSidecarExpanded(false);
      }
    };
    const handleModalHidden = () => {
      resetNoteCloseGuardState();
      isPatientModalOpenRef.current = false;
      document.body.classList.remove("edit-patient-modal-open");
      stopNoteDictation({ suppressStoppedToast: true, clearStatus: true });
      setIsPatientModalOpen(false);
      // Any close path (incl. Escape) must clear editing so the parent
      // patient-list polling resumes and displayed data doesn't go stale.
      setIsEditing(false);
    };
    // Guard closes that bypass the Close/X buttons (e.g. the Escape key).
    // Close/X run their own confirmation and flag noteCloseConfirmedRef so they
    // aren't re-prompted. When Escape hits an unsaved note we block Bootstrap's
    // close and defer to the confirmation modal; confirming runs a programmatic
    // dismiss (which sets noteCloseConfirmedRef so the follow-up hide proceeds).
    const handleModalHide = (event) => {
      if (noteCloseConfirmedRef.current) {
        return;
      }
      if (hasUnsavedReviewNote()) {
        event.preventDefault();
        pendingReviewCloseRef.current = () => {
          noteCloseConfirmedRef.current = true;
          modalDismissRef.current?.click();
        };
        setUnsavedCloseConfirmOpen(true);
      }
    };

    modalElement.addEventListener("show.bs.modal", handleModalShow);
    modalElement.addEventListener("shown.bs.modal", handleModalShown);
    modalElement.addEventListener("hidden.bs.modal", handleModalHidden);
    modalElement.addEventListener("hide.bs.modal", handleModalHide);

    return () => {
      modalElement.removeEventListener("show.bs.modal", handleModalShow);
      modalElement.removeEventListener("shown.bs.modal", handleModalShown);
      modalElement.removeEventListener("hidden.bs.modal", handleModalHidden);
      modalElement.removeEventListener("hide.bs.modal", handleModalHide);
    };
  }, [patient.patient_id, isPatientChatSplitView, stopNoteDictation, setIsEditing]);

  useEffect(
    () => () => {
      if (isPatientModalOpenRef.current) {
        document.body.classList.remove("edit-patient-modal-open");
        // This modal can unmount mid-hide: finalizing flips the patient status
        // Review -> Locked, which renders a different EditPatient branch and tears
        // down this modal's DOM before Bootstrap finishes hiding. Bootstrap appends
        // the backdrop to <body> (outside React), so it is left orphaned and
        // intercepts pointer events. Clear it once the DOM settles, unless another
        // modal is genuinely still open.
        setTimeout(() => {
          if (!document.querySelector(".modal.show")) {
            document.querySelectorAll(".modal-backdrop").forEach((backdrop) => backdrop.remove());
            document.body.classList.remove("modal-open");
            document.body.style.removeProperty("overflow");
            document.body.style.removeProperty("padding-right");
          }
        }, 0);
      }
    },
    []
  );

  useEffect(() => {
    if (isPatientModalOpen && !isPatientChatSplitView) {
      setIsPatientChatSidecarExpanded(false);
    }
  }, [isPatientChatSplitView, isPatientModalOpen]);

  // Add new function to fetch complete patient data
  const fetchCompletePatientData = async (patientId, dateOfAppt) => {
    setIsFetching(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/dashboard/`, {
        method: "GET",
        headers: {
          token: getSessionToken(),
          pickedDate: dateOfAppt,
        },
      });

      const data = await response.json();

      // Bail out if a different patient's modal was opened while we were fetching
      if (openedPatientIdRef.current !== patientId) {
        Sentry.captureMessage(
          `fetchCompletePatientData: patient changed from ${patientId} to ${openedPatientIdRef.current}, discarding stale data`,
          "warning"
        );
        return;
      }

      // Find the specific patient in the response
      const completePatientData = data.find((p) => p.patient_id === patientId);
      if (completePatientData) {
        setDescription(completePatientData.description);
        setNote(completePatientData.note);
        setSavedDbNote(completePatientData.note || "");
        setIsDashboardDataFetched(true);
        setCodingInfo(completePatientData.coding_information);
        setLOSCode(completePatientData.los_code);
        setEncounterTime(completePatientData.time_spent);
        setTimeBasedCode(completePatientData.time_based_code);
        setICD_10_codes(completePatientData.icd_10_codes);
        setOrders(completePatientData.orders);
        setOrdersV2(completePatientData.orders_v2);
        setDiarizedTranscript(completePatientData.diarized_transcript);
        setDiarizedTranscriptCsvUrl(completePatientData.diarized_transcript_csv_url);
        setCitedNoteData(completePatientData.cited_note);
        setCPT_codes(completePatientData.cpt_codes);
        setHCC_Codes(completePatientData.hcc_codes);
        const loadedSelectedCodes = formatICD10Codes(completePatientData.icd10_codes_v2);
        setSelectedCodes(loadedSelectedCodes);
        setOriginalCodes(completePatientData.icd10_codes_v2);
        setMdmSuggestion(completePatientData.mdm_suggested);
        setEMData(completePatientData.e_and_m);
        setOriginalEmData(completePatientData.e_and_m);

        setMacros(completePatientData.macros);
        setChartHistory(completePatientData.chart_history);
        const loadedPatientSummary =
          typeof completePatientData.patient_summary === "string"
            ? completePatientData.patient_summary
            : "";
        setPatientSummary(loadedPatientSummary);

        // Snapshot what the form was initialized with
        // Deep-clone objects and store fields post-coercion ("" not null) so save can detect real edits
        const cloneValue = (value) => (value == null ? value : JSON.parse(JSON.stringify(value)));
        loadedSnapshotRef.current = {
          note: completePatientData.note,
          encounterTime: completePatientData.time_spent,
          macros: cloneValue(completePatientData.macros),
          patientSummary: loadedPatientSummary,
          selectedCodes: cloneValue(loadedSelectedCodes),
        };
      }
    } catch (err) {
      console.error("Error fetching complete patient data:", err.message);
    } finally {
      setIsFetching(false);
      // Once the fetch settles (even on failure) the editor shows what the user sees, so a
      // cleared field must reach Smart Assist as cleared instead of falling back to saved text.
      if (openedPatientIdRef.current === patientId) {
        setIsNoteLoaded(true);
      }
    }
  };

  const isSidecarRendered = isPatientModalOpen && Boolean(patientChatCapabilitiesQuery.data?.enabled);
  useEffect(() => {
    isSidecarRenderedRef.current = isSidecarRendered;
    if (isSidecarRendered) {
      patientModalRef.current?.removeAttribute("aria-modal");
    }
  }, [isSidecarRendered]);
  // data-bs-focus="false" also turns off Bootstrap's focus trap, and this modal
  // isn't portaled, so Tab would otherwise reach the patient list behind it.
  useTabWrap(isPatientModalOpen, () => [
    patientModalRef.current,
    document.getElementById(`ask-knowtex-sidecar-${patient.patient_id}`)?.closest("aside"),
  ]);
  const hasCitationsTab = Boolean(citedNoteData);
  const navTabs = useMemo(
    () => [
      "home",
      "diarized",
      ...(hasCitationsTab ? ["citations"] : []),
      ...(showPatientSummary ? ["summary"] : []),
    ],
    [hasCitationsTab, showPatientSummary]
  );
  const { onKeyDown: handleNavTabKeyDown, getTabIndex: getNavTabIndex } = useTabListKeyboard(
    navTabs,
    effectiveNavTab,
    (tab) => {
      setActiveNavTab(tab);
      sendTabSelectLog(NAV_TAB_LOG_NAMES[tab]);
    }
  );
  // Every Review/Locked row keeps its modal mounted, so fixed ids (used by the
  // tutorial and e2e tests) are only set on the open one to keep them unique.
  const openModalId = (id) => (isPatientModalOpen ? id : undefined);

  // Guard: do not render if no valid patient
  if (!patient || !patient.patient_id) {
    return null;
  }

  //orders server logging

  const sendOrdersLog = (mrn, patientId, order) => {
    //server logging
    sendServerLogging({
      body: {
        ...userJSON(localStorage.getItem("userName")),
        event: "ADD_TEST_ORDER",
        eventData: {
          MRN: mrn,
          Patient_Id: patientId,
          Order: order,
        },
      },
    });
  };

  const sendDeleteOrdersLog = (mrn, patientId, order) => {
    //server logging
    sendServerLogging({
      body: {
        ...userJSON(localStorage.getItem("userName")),
        event: "DELETE_TEST_ORDER",
        eventData: {
          MRN: mrn,
          Patient_Id: patientId,
          Order: order,
        },
      },
    });
  };

  const sendOpenNoteLog = (mrn, patientId, status) => {
    //server logging
    sendServerLogging({
      body: {
        ...userJSON(localStorage.getItem("userName")),
        event: "OPEN_NOTE",
        eventData: {
          MRN: mrn,
          Patient_Id: patientId,
          Status: status,
        },
      },
    });
  };

  const sendAddMedsLog = (mrn, patientId, medication) => {
    //server logging
    sendServerLogging({
      body: {
        ...userJSON(localStorage.getItem("userName")),
        event: "ADD_MEDICATION_ORDER",
        eventData: {
          MRN: mrn,
          Patient_Id: patientId,
          Medication: medication,
        },
      },
    });
  };

  const sendDeleteMedsLog = (mrn, patientId, medication) => {
    //server logging
    sendServerLogging({
      body: {
        ...userJSON(localStorage.getItem("userName")),
        event: "DELETE_MEDICATION_ORDER",
        eventData: {
          MRN: mrn,
          Patient_Id: patientId,
          Medication: medication,
        },
      },
    });
  };

  const sendAddRadiologyLog = (mrn, patientId, radOrder) => {
    //server logging
    sendServerLogging({
      body: {
        ...userJSON(localStorage.getItem("userName")),
        event: "ADD_RADIOLOGY_ORDER",
        eventData: {
          MRN: mrn,
          Patient_Id: patientId,
          Radiology_Order: radOrder,
        },
      },
    });
  };

  const sendDeleteRadiologyLog = (mrn, patientId, radOrder) => {
    //server logging
    sendServerLogging({
      body: {
        ...userJSON(localStorage.getItem("userName")),
        event: "DELETE_RADIOLOGY_ORDER",
        eventData: {
          MRN: mrn,
          Patient_Id: patientId,
          Radiology_Order: radOrder,
        },
      },
    });
  };

  // server logging for save and upload event
  const sendSaveUploadLog = (mrn, patientId, original, updated) => {
    sendServerLogging({
      body: {
        ...userJSON(localStorage.getItem("userName")),
        event: "SAVE_AND_UPLOAD_NOTE",
        eventData: {
          MRN: mrn,
          Patient_Id: patientId,
          Original_Text: original,
          Updated_Text: updated,
        },
      },
    });
  };

  //server logging for ONLY save event
  const sendSaveLog = (mrn, patientId, original, updated) => {
    sendServerLogging({
      body: {
        ...userJSON(localStorage.getItem("userName")),
        event: "SAVE_NOTE",
        eventData: {
          MRN: mrn,
          Patient_Id: patientId,
          Original_Text: original,
          Updated_Text: updated,
        },
      },
    });
  };

  const sendTabSelectLog = (tab) => {
    sendServerLogging({
      body: {
        ...userJSON(localStorage.getItem("userName")),
        event: "SELECT_TAB",
        eventData: {
          current: tab,
        },
      },
    });
  };

  const sendCloseNoteLog = (mrn, patientId, status) => {
    //server logging
    sendServerLogging({
      body: {
        ...userJSON(localStorage.getItem("userName")),
        event: "CLOSE_NOTE",
        eventData: {
          MRN: mrn,
          Patient_Id: patientId,
          Status: status,
        },
      },
    });
  };

  const sendSlackNotification = async (event, patientData) => {
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/auth/slack-notification`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          token: getSessionToken(),
        },
        body: JSON.stringify({
          event,
          patientData,
          userData: {
            userName: localStorage.getItem("userName"),
          },
        }),
      });
    } catch (err) {
      // Don't block the main flow if Slack fails
      console.error("Failed to send Slack notification:", err);
    }
  };

  return (
    <Fragment>
      <ConfirmationModal
        open={unsavedCloseConfirmOpen}
        title="Unsaved changes"
        continueText="Close without saving"
        cancelText="Keep editing"
        continueVariant="primary-danger"
        onContinue={handleUnsavedCloseContinue}
        onCancel={handleUnsavedCloseCancel}
      >
        {NOTE_UNSAVED_CHANGES_CONFIRM_MESSAGE}
      </ConfirmationModal>
      <div
        className="flex items-center hover:opacity-80"
        onClick={() => {
          sendOpenNoteLog(patient.mrn, patient.patient_id, patient.status);
          updateProviderReviewed(patient.patient_id);
        }}
      >
        <div className="pr-2">
          <svg
            className="w-6 h-6 text-background1"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M15 4h3c.6 0 1 .4 1 1v15c0 .6-.4 1-1 1H6a1 1 0 01-1-1V5c0-.6.4-1 1-1h3m0 3h6m-6 7l2 2 4-4m-5-9v4h4V3h-4z"
            />
          </svg>
        </div>

        {/* Here we show "Review" or "Locked" plus (Added) sub-status if uploading or uploaded */}
        <button
          id={isTutorialReviewAnchor ? "review_note" : undefined}
          type="button"
          className={`text-xl ${
            status === "Review"
              ? isReadOnlyCancelled
                ? "flex items-center text-[#fff0f0]"
                : ""
              : "flex items-center text-[#fff0f0]"
          } ${isTutorialActive && currentStepId === "locked_note" ? "pointer-events-none opacity-50 cursor-not-allowed" : ""}`}
          data-bs-toggle="modal"
          data-bs-target={`#id${patient.patient_id}`}
          disabled={isTutorialActive && currentStepId === "locked_note"}
          onClick={async () => {
            // Prevent click if tutorial is active on locked_note step
            if (isTutorialActive && currentStepId === "locked_note") {
              return;
            }
            // Set isEditing immediately to stop Dashboard polling before any async work.
            setIsEditing(status !== "Locked");
            setIsSummaryEditorActive(false);
            openedPatientIdRef.current = patient.patient_id;
            setIsNoteLoaded(false);
            setIsDashboardDataFetched(false);
            setDescription(patient.description);
            setPatientSummary(patient.patient_summary || "");
            setCopiedSummary(false);
            await updateProviderReviewed(patient.patient_id, { force: true });
            await fetchCompletePatientData(patient.patient_id, patient.date_of_appt);
            log_review_note(patient.patient_id);
            sendOpenNoteLog(patient.mrn, patient.patient_id, patient.status);
            windowMessenger.startKeepalive(userId, 300000); // 5 minutes
            trackEvent(
              Events.PATIENT_REVIEW_INITIATED,
              { patient_id: patient.patient_id },
              { groups: { patient: patient.patient_id } }
            );
          }}
        >
          {/* Tab-only screen reader users don't hear the card's heading, so the
              name tells them which patient this opens. */}
          {status === "Review" ? (
            <>
              {isReadOnlyCancelled ? "Review (View Only)" : "Review"}
              <span className="sr-only">{` for ${patient.description}`}</span>
            </>
          ) : (
            <div>
              Locked (View Only)
              <span className="sr-only">{` for ${patient.description}`}</span>
              {ehrUploadStatus === "uploading" && (
                <div className="text-white text-sm">Uploading to EHR...</div>
              )}
              {ehrUploadStatus === "success" && (
                <div className="text-white text-sm">Uploaded to EHR</div>
              )}
            </div>
          )}
        </button>
      </div>

      {isSidecarRendered && (
        <AskKnowtexSidecar
          isExpanded={isPatientChatSidecarExpanded}
          onToggle={() => setPatientChatSidecarPreferredExpanded(!isPatientChatSidecarExpanded)}
          sidecarId={`ask-knowtex-sidecar-${patient.patient_id}`}
          widthPercent={patientChatWidthPercent}
          onWidthChange={setPatientChatWidthPercent}
          isFixedPanel={isPatientChatSplitView}
          contentScale={patientChatContentScale}
        >
          <SmartAssistPanel
            patientId={patient.patient_id}
            capabilities={patientChatCapabilitiesQuery.data}
            draftSaver={smartAssistDraftSaver}
            stateCacheRef={smartAssistStateCacheRef}
            note={note}
            patientSummary={patientSummary}
            diarizedTranscript={isDashboardDataFetched ? diarizedTranscript : undefined}
            chartHistory={isDashboardDataFetched ? chartHistory : undefined}
            sendEmptyContext={isNoteLoaded}
            dotPhraseMacrosByPhrase={smartAssistMacrosByDotPhrase}
            isConflictingDictationActive={isNoteDictating || isNoteDictationStarting}
            conflictingDictationMessage="Stop note dictation before starting Smart Assist dictation."
            onDictationStateChange={setSmartAssistDictationState}
            onUseAsNote={isLocked ? undefined : handleUseSmartAssistReplyAsNote}
            isHidden={!isPatientChatSidecarExpanded}
            onMinimize={() => setPatientChatSidecarPreferredExpanded(false)}
            minimizeControlsId={`ask-knowtex-sidecar-${patient.patient_id}`}
            quickPrompts={REVIEW_SMART_ASSIST_PROMPTS}
            showCreatePrompt={false}
            helpText={REVIEW_SMART_ASSIST_HELP_TEXT}
            helpContent={REVIEW_SMART_ASSIST_HELP_CONTENT}
            description={null}
            emptyState={REVIEW_SMART_ASSIST_EMPTY_STATE}
            footerActions={
              (hasHighlightSources || (note || "") !== (savedDbNote || "")) && (
                <>
                  {hasHighlightSources && (
                    <div className="flex flex-wrap items-center gap-2">
                      <label
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-accent1"
                        title="Highlight note text that matches selected chart history or Additional EHR Context."
                      >
                        <input
                          type="checkbox"
                          checked={highlightSourceText}
                          onChange={(event) => setHighlightSourceText(event.target.checked)}
                          className="h-3.5 w-3.5 rounded border-gray-300 text-accent1 focus:ring-accent1"
                        />
                        Highlight carry forward chart text in note
                      </label>
                      {isHighlighting && (
                        <span role="status" className="text-xs text-light-grey">
                          Highlighting…
                        </span>
                      )}
                    </div>
                  )}
                  {(note || "") !== (savedDbNote || "") && (
                    <KxButton
                      type="button"
                      variant="secondary"
                      size="small"
                      className="ml-auto"
                      onClick={handleResetNoteToSavedVersion}
                    >
                      Reset to initial note
                    </KxButton>
                  )}
                </>
              )
            }
          />
        </AskKnowtexSidecar>
      )}

      <div
        className="modal fade modal-xl h-full"
        id={`id${patient.patient_id}`}
        ref={patientModalRef}
        tabIndex="-1"
        aria-labelledby={`editModalLabel-${patient.patient_id}`}
        aria-hidden={isPatientModalOpen ? "false" : "true"}
        // The Smart Assist sidecar renders outside this dialog, so declaring it
        // modal would hide the sidecar from screen readers. Bootstrap also sets
        // this on show; the shown handler and the sidecar effect clear it.
        aria-modal={isPatientModalOpen && !isSidecarRendered ? "true" : undefined}
        data-bs-backdrop="static"
        data-bs-focus="false"
        role="dialog"
      >
        <button ref={modalDismissRef} data-bs-dismiss="modal" hidden aria-hidden="true" />
        <div
          className={`modal-dialog modal-xl ${css.patientModalWide} ${
            isPatientModalOpen &&
            patientChatCapabilitiesQuery.data?.enabled &&
            isPatientChatSplitView
              ? isPatientChatSidecarExpanded
                ? css.patientModalWithSmartAssist
                : css.patientModalWithMinimizedSmartAssist
              : ""
          }`}
          style={{ "--smart-assist-panel-width": `${patientChatWidthPercent}vw` }}
        >
          <div className={`modal-content border-0 shadow-none ${css.editPatientContent}`}>
            <div className="modal-header">
              <h2
                ref={modalTitleRef}
                tabIndex={-1}
                className="text-accent1 text-3xl font-bold focus:outline-none"
                id={`editModalLabel-${patient.patient_id}`}
              >
                <span className="inline-flex items-center gap-2">
                  <span>{description || patient.description}</span>
                  {isLocked && (
                    <span className="inline-flex items-center rounded-full bg-yellow-100 px-2 py-1 text-sm font-semibold text-yellow-800">
                      Read only
                    </span>
                  )}
                  {isMockPatient && (
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-yellow-100 text-yellow-800 text-sm font-semibold px-2 py-1"
                      title="Mock patient used for tutorial workflows"
                    >
                      Mock Patient
                      <svg
                        className="w-4 h-4"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
                      </svg>
                    </span>
                  )}
                </span>
                <br />
                {patient.demographics}
              </h2>
              <button
                type="button"
                className="mt-0 mb-3"
                aria-label="Close Review Note modal"
                title="Close Review Note modal"
                onClick={() => {
                  requestReviewClose(() => {
                    setDescription(patient.description);
                    setNote(savedDbNote || "");
                    setCodingInfo(patient.coding_information);
                    setLOSCode(patient.los_code);
                    log_review_note(patient.patient_id);
                    setEncounterTime(patient.time_spent);
                    setTimeBasedCode(patient.time_based_code);
                    setICD_10_codes(patient.icd_10_codes);
                    setOrders(patient.orders);
                    setOrdersV2(patient.orders_v2);
                    setIsCopied(false);
                    setIsTranscriptCopied(false);
                    setCopiedSummary(false);
                    setIsSummaryEditorActive(false);
                    // set isEditing back to false on close button
                    setIsEditing(false);
                    setDiarizedTranscript(patient.diarized_transcript);
                    setDiarizedTranscriptCsvUrl(patient.diarized_transcript_csv_url);
                    setCitedNoteData(patient.cited_note);
                    setPatientSummary(patient.patient_summary || "");
                    setCPT_codes(patient.cpt_codes);
                    setHCC_Codes(patient.hcc_codes);
                    setSelectedCodes(selectedCodes);
                    setEMData(patient.e_and_m);

                    sendCloseNoteLog(patient.mrn, patient.patient_id, patient.status);
                    windowMessenger.stopKeepalive();
                    dismissBootstrapModal();
                  });
                }}
              >
                <svg
                  className="w-6 h-6 text-gray-800 dark:text-white"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="m15 9-6 6m0-6 6 6m6-3a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                  />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
                {copyAnnouncement}
              </div>
              <div>
                <nav>
                  <div
                    className="text-black flex mb-0 whitespace-nowrap overflow-x-auto "
                    id={`id${patient.patient_id}nav-tab`}
                    role="tablist"
                    aria-label="Note sections"
                    onKeyDown={handleNavTabKeyDown}
                  >
                    <button
                      className={`nav-link pt-2 pl-11 pr-11 pb-2 mr-4 rounded-t-md focus:bg-accent1 focus:text-background1 font-bold ${getTabButtonClass("home")}`}
                      id={`id${patient.patient_id}nav-home-tab`}
                      type="button"
                      role="tab"
                      aria-label="Note tab"
                      title="Note tab"
                      aria-controls={`id${patient.patient_id}nav-home`}
                      aria-selected={effectiveNavTab === "home"}
                      tabIndex={getNavTabIndex("home")}
                      onClick={() => {
                        setActiveNavTab("home");
                        sendTabSelectLog("Note");
                      }}
                    >
                      Note
                    </button>

                    {/* Orders tab component render */}
                    {/* <button
                      className="nav-link border pt-2 pl-11 pr-11 pb-2 mr-4 rounded-t-md focus:bg-accent1 focus:text-background1 text-accent1  bg-secondary-grey font-bold"
                      id={`id${patient.patient_id}nav-orders-tab`}
                      data-bs-toggle="tab"
                      data-bs-target={`#id${patient.patient_id}nav-orders`}
                      type="button"
                      role="tab"
                      aria-controls="nav-orders"
                      aria-selected="false"
                      onClick={() => {
                        sendTabSelectLog("orders");
                      }}
                    >
                      Orders ({ordersCount ? ordersCount : 0})
                    </button> */}

                    <button
                      className={`nav-link pt-2 pl-11 pr-11 pb-2 mr-4 rounded-t-md focus:bg-accent1 focus:text-background1 font-bold ${getTabButtonClass("diarized")}`}
                      id={`id${patient.patient_id}nav-diarized-tab`}
                      type="button"
                      role="tab"
                      aria-label="Transcript tab"
                      title="Transcript tab"
                      aria-controls={`id${patient.patient_id}nav-diarized`}
                      aria-selected={effectiveNavTab === "diarized"}
                      tabIndex={getNavTabIndex("diarized")}
                      onClick={() => {
                        setActiveNavTab("diarized");
                        sendTabSelectLog("Transcript");
                      }}
                    >
                      Transcript
                    </button>

                    {citedNoteData && (
                      <button
                        className={`nav-link pt-2 pl-11 pr-11 pb-2 mr-4 rounded-t-md focus:bg-accent1 focus:text-background1 font-bold ${getTabButtonClass("citations")}`}
                        id={`id${patient.patient_id}nav-citations-tab`}
                        type="button"
                        role="tab"
                        aria-label="Note citations tab"
                        title="Note citations tab"
                        aria-controls={`id${patient.patient_id}nav-citations`}
                        aria-selected={effectiveNavTab === "citations"}
                      tabIndex={getNavTabIndex("citations")}
                        onClick={() => {
                          setActiveNavTab("citations");
                          sendTabSelectLog("Note Citations");
                        }}
                      >
                        Note Citations
                      </button>
                    )}

                    {/* show patient summary tab only to VA users */}
                    {showPatientSummary && (
                      <button
                        className={`nav-link pt-2 pl-11 pr-11 pb-2 mr-4 rounded-t-md focus:bg-accent1 focus:text-background1 font-bold ${getTabButtonClass("summary")}`}
                        id={`id${patient.patient_id}nav-summary-tab`}
                        type="button"
                        role="tab"
                        aria-label="Patient visit summary tab"
                        title="Patient visit summary tab"
                        aria-controls={`id${patient.patient_id}nav-summary`}
                        aria-selected={effectiveNavTab === "summary"}
                      tabIndex={getNavTabIndex("summary")}
                        onClick={() => {
                          setActiveNavTab("summary");
                          sendTabSelectLog("Patient Visit Summary");
                        }}
                      >
                        Patient Visit Summary
                      </button>
                    )}
                  </div>
                </nav>
                <div
                  className="tab-content p-4 border-t border-t-gray-200 border border-x-gray-200 rounded-b-lg"
                  id={openModalId("nav-tabContent")}
                >
                  <div
                    className={`tab-pane fade ${effectiveNavTab === "home" ? "show active" : ""}`}
                    id={`id${patient.patient_id}nav-home`}
                    role="tabpanel"
                    aria-labelledby={`id${patient.patient_id}nav-home-tab`}
                  >
                    <div id={openModalId("review_note_page")}>
                      {/* PLACEHOLDER FOR NOTE TITLE DROPDOWN: <div className="flex justify-between items-end mt-3 mb-3">
                        <div className="w-64">
                          <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                            Select Note Title <span className="text-red-500">* (Required)</span>
                          </label>
                          <select className="w-full py-3.5 pl-5 pr-11 text-sm border border-gray-200 rounded-full bg-gray-50 text-gray-800 appearance-none cursor-pointer shadow-sm hover:shadow-md transition-all focus:outline-none focus:border-indigo-300 focus:bg-white focus:ring-[3px] focus:ring-indigo-100 bg-[url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%236b7280%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpath%20d%3D%22M6%209l6%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[right_18px_center]">
                            <option value="">Select a note...</option>
                            <option value="1">Note Title 1</option>
                            <option value="2">Note Title 2</option>
                            <option value="3">Note Title 3</option>
                            <option value="4">Note Title 4</option>
                            <option value="5">Note Title 5</option>
                            <option value="6">Note Title 6</option>
                            <option value="7">Note Title 7</option>
                            <option value="8">Note Title 8</option>
                            <option value="9">Note Title 9</option>
                            <option value="10">Note Title 10</option>
                          </select>
                        </div>

                        <CopyToClipboard
                          text={note}
                          onCopy={() => onCopy(patient.patient_id)}
                        >
                          {isCopied ? (
                            <div className="text-success text-sm">✓ Copied!</div>
                          ) : (
                            <div className="flex">
                              <img
                                src={clipboard}
                                width="20"
                                height="20"
                                alt="copy"
                                className="mr-1 hover:cursor-pointer"
                              />
                              <h1 className="text-accent1 hover:cursor-pointer">
                                Copy Note
                              </h1>
                            </div>
                          )}
                        </CopyToClipboard>
                      </div> */}
                      <div className="mb-3 flex items-start justify-between gap-3">
                        {!isStandaloneUser &&
                          !isLocked &&
                          canRefreshVitalsFromSource &&
                          showVitalsOutdatedNotice && (
                            <div className="inline-flex min-w-0 w-fit max-w-full flex-col gap-2 rounded-md border border-green-200 bg-green-50 px-2.5 py-2">
                              <div className="flex max-w-full flex-wrap items-center gap-2">
                                <p className="m-0 text-xs font-semibold text-green-800">
                                  Vitals are outdated, refresh to get latest vitals
                                </p>
                                <span
                                  className={
                                    isCrossSiteCare ? "inline-flex cursor-not-allowed" : ""
                                  }
                                  title={isCrossSiteCare ? crossSiteRelaunchMessage : undefined}
                                >
                                  <button
                                    type="button"
                                    onClick={handleRefreshVitals}
                                    disabled={isRefreshingVitals || isLocked || isCrossSiteCare}
                                    className={`rounded px-2.5 py-1.5 text-xs font-semibold transition-colors ${
                                      isRefreshingVitals || isLocked || isCrossSiteCare
                                        ? "cursor-not-allowed bg-gray-300 text-gray-700"
                                        : "bg-ktx-green text-white hover:bg-green-700"
                                    }`}
                                  >
                                    {isRefreshingVitals ? "Refreshing..." : "Refresh Vitals"}
                                  </button>
                                </span>
                                {hasLatestChartHistoryVitals ? (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setIsVitalsFallbackExpanded((previous) => !previous)
                                      }
                                      className="rounded border border-green-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-green-800 transition-colors hover:bg-green-100"
                                    >
                                      {isVitalsFallbackExpanded
                                        ? "Hide Current Vitals"
                                        : "View Current Vitals"}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={handleCopyCurrentVitals}
                                      className="rounded border border-green-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-green-800 transition-colors hover:bg-green-100"
                                    >
                                      Copy Current Vitals
                                    </button>
                                  </>
                                ) : null}
                              </div>
                              {isVitalsFallbackExpanded && hasLatestChartHistoryVitals ? (
                                <div className="w-full rounded border border-green-200 bg-white p-2">
                                  <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-green-700">
                                    Current chart_history vitals
                                  </p>
                                  <pre className="m-0 max-h-36 overflow-auto whitespace-pre-wrap break-words text-xs text-green-900">
                                    {latestChartHistoryVitals}
                                  </pre>
                                </div>
                              ) : null}
                            </div>
                          )}
                        <div className="ml-auto mt-1.5 flex min-h-8 shrink-0 items-center">
                          <div className="flex items-center gap-2">
                            {activeNoteDictationTarget === "note" &&
                              (isNoteDictating || isNoteDictationStarting) &&
                              noteDictationStatus && (
                                <span
                                  className="inline-flex items-center gap-1.5 text-xs font-medium text-light-grey"
                                  role="status"
                                  aria-live="polite"
                                >
                                  <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                                  {noteDictationStatus}
                                </span>
                              )}
                            <div className="inline-flex h-8 items-stretch overflow-hidden rounded-md border border-gray-200 bg-white shadow-sm">
                              <button
                                type="button"
                                title={
                                  isNoteDictating && activeNoteDictationTarget === "note"
                                    ? "Stop note dictation"
                                    : "Dictate into clinical note"
                                }
                                onMouseDown={() => {
                                  const activeTextarea = textareaRef.current;
                                  const hasPlacedCaret =
                                    activeTextarea && document.activeElement === activeTextarea;
                                  const fallbackPosition = hasPlacedCaret
                                    ? Math.min(cursorPosition, noteRef.current.length)
                                    : 0;
                                  noteDictationSelectionRef.current = {
                                    start: hasPlacedCaret
                                      ? (activeTextarea.selectionStart ?? fallbackPosition)
                                      : 0,
                                    end: hasPlacedCaret
                                      ? (activeTextarea.selectionEnd ?? fallbackPosition)
                                      : 0,
                                  };
                                  if (!hasPlacedCaret && activeTextarea) {
                                    activeTextarea.setSelectionRange(0, 0);
                                    activeTextarea.scrollTop = 0;
                                  }
                                }}
                                onClick={() => handleToggleNoteDictation("note")}
                                disabled={
                                  isNoteDictationStarting ||
                                  (isLocked &&
                                    !(isNoteDictating && activeNoteDictationTarget === "note"))
                                }
                                className={`inline-flex min-w-8 items-center justify-center gap-1.5 border-r border-gray-200 px-2 text-xs font-semibold transition-colors ${
                                  isNoteDictating && activeNoteDictationTarget === "note"
                                    ? "bg-red-600 text-white hover:bg-red-700"
                                    : isLocked || isNoteDictationStarting
                                      ? "cursor-not-allowed bg-gray-100 text-gray-400"
                                      : "text-accent1 hover:bg-secondary-grey"
                                }`}
                                aria-label={
                                  isNoteDictating && activeNoteDictationTarget === "note"
                                    ? "Stop note dictation"
                                    : "Start note dictation"
                                }
                              >
                                {isNoteDictating && activeNoteDictationTarget === "note" ? (
                                  <>
                                    <Square className="h-3.5 w-3.5" />
                                    <span>Stop</span>
                                  </>
                                ) : (
                                  <>
                                    <Mic className="h-3.5 w-3.5" />
                                    <span>Dictate</span>
                                  </>
                                )}
                              </button>
                              <CopyToClipboard
                                text={note}
                                onCopy={() => onCopy(patient.patient_id)}
                              >
                                <button
                                  type="button"
                                  aria-label="Copy note"
                                  className={`inline-flex items-center gap-1.5 px-2 text-xs font-semibold transition-colors hover:bg-secondary-grey ${
                                    isCopied ? "text-success" : "text-accent1"
                                  }`}
                                >
                                  {isCopied ? (
                                    <span>✓ Copied</span>
                                  ) : (
                                    <>
                                      <img src={clipboard} width="14" height="14" alt="" />
                                      <span>Copy</span>
                                    </>
                                  )}
                                </button>
                              </CopyToClipboard>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="mt-2">
                        <div className="min-w-0">
                          <div className="relative rounded-md bg-secondary-grey shadow-knowtex-shadow">
                            {noteHighlightRanges.length > 0 && (
                              <div
                                ref={noteHighlightRef}
                                aria-hidden="true"
                                className="form-control pointer-events-none absolute left-0 top-0 overflow-hidden resize-none rounded-md border-0 bg-secondary-grey text-transparent shadow-none"
                                style={{
                                  overflowWrap: "break-word",
                                  whiteSpace: "pre-wrap",
                                }}
                              >
                                <div ref={noteHighlightContentRef}>
                                  {renderPlaceholderHighlights(note, noteHighlightRanges)}
                                  {note.endsWith("\n") ? "\u00a0" : null}
                                </div>
                              </div>
                            )}
                            <textarea
                              id={`note-editor-${patient.patient_id}`}
                              ref={textareaRef}
                              type="text"
                              aria-label="Clinical note editor"
                              aria-describedby={`note-editor-keyboard-help-${patient.patient_id}`}
                              aria-readonly={isLocked || !isNoteEditorActive}
                              readOnly={isLocked || !isNoteEditorActive}
                              disabled={isLocked}
                              className={`form-control relative z-10 resize-none rounded-md border-0 text-accent1 ${
                                isLocked ? "cursor-not-allowed opacity-70" : ""
                              }`}
                              style={{ backgroundColor: "transparent" }}
                              value={note || ""}
                              onChange={(e) => {
                                const nextNote = e.target.value;
                                syncEditorAfterProgrammaticEdit({
                                  target: "note",
                                  previousValue: noteRef.current,
                                  nextValue: nextNote,
                                  selectionStart: e.target.selectionStart,
                                  selectionEnd: e.target.selectionEnd,
                                });
                                setNote(nextNote);
                                setIsCopied(false);
                                updateNoteCursorPosition(e);
                              }}
                              onClick={(e) => {
                                if (!isLocked) {
                                  setIsNoteEditorActive(true);
                                }
                                setCursorPosition(e.target.selectionStart);
                                selectVvcPlaceholderAtCursor(e.target);

                                sendClickTextAreaLog(patient.mrn, patient.patient_id);
                              }}
                              onBlur={(e) => {
                                updateNoteCursorPosition(e);
                                setIsNoteEditorActive(false);
                              }}
                              onKeyDown={handleNoteKeyDown}
                              onKeyUp={updateNoteCursorPosition}
                              onSelect={updateNoteCursorPosition}
                              onMouseUp={handleNoteSelection}
                              onScroll={(e) => {
                                if (noteHighlightContentRef.current) {
                                  noteHighlightContentRef.current.style.transform = `translate(${-e.currentTarget.scrollLeft}px, ${-e.currentTarget.scrollTop}px)`;
                                }
                              }}
                              rows="30"
                            ></textarea>
                          </div>
                          {vvcPlaceholderRanges.length > 0 && (
                            <p className="mt-2 text-sm font-semibold text-amber-700" role="status">
                              {vvcPlaceholderRanges.length} VVC placeholder
                              {vvcPlaceholderRanges.length === 1 ? "" : "s"} highlighted. Click a
                              highlighted prompt and type to replace it.
                            </p>
                          )}
                          <p
                            id={`note-editor-keyboard-help-${patient.patient_id}`}
                            className="sr-only"
                          >
                            Clinical note editor. Press Enter or Space to enable editing. To leave
                            this text area, press Shift plus Tab. VVC location and VA facility
                            placeholders are highlighted and selected when clicked.
                          </p>
                          <div aria-live="assertive" aria-atomic="true" className="sr-only">
                            {isNoteEditorActive
                              ? "Editor is enabled. You can now edit the note."
                              : ""}
                          </div>
                          {/* Macros section */}
                          <div className="flex flex-col mt-3">
                            <div className="relative inline-block" ref={tooltipRef}>
                              <div className="flex items-center mb-4 gap-2">
                                <h2 className="text-xl font-semibold text-knowtex-grey">
                                  Dot Phrases:
                                </h2>

                                <p className="text-sm italic text-light-grey">
                                  Click where you'd like to insert a macro, then make your selection
                                  below.
                                </p>
                              </div>
                            </div>

                            <div>
                              <MacrosFeature
                                macros={macros}
                                setMacros={setMacros}
                                setNote={setNote}
                                cursorPosition={cursorPosition}
                                setCursorPosition={setCursorPosition}
                                textareaRef={textareaRef}
                                summaryTextareaRef={summaryTextareaRef}
                                setPatientSummary={setPatientSummaryFromDotPhrase}
                                name={name}
                                status={status}
                                patient={patient}
                              />
                            </div>
                          </div>

                          {/* New ICD 10 Codes Section */}
                          <div className="flex flex-col mt-3">
                            <div className="flex items-center gap-2">
                              <h2 className="text-accent1 text-xl font-medium mb-2">
                                ICD-10 Codes
                              </h2>
                              {historicalCodes.length > 0 && (
                                <span className="text-gray-500 text-sm">
                                  Historic codes from the EMR have been filtered out from this list.
                                </span>
                              )}
                            </div>
                            <div className="flex flex-row space-x-60">
                              {/* Primary Codes */}
                              <div className="flex flex-col items-start">
                                <div className="flex flex-row items-start">
                                  <h2 className=" text-accent1 text-lg font-bold mb-2 mr-4">
                                    Primary Codes
                                  </h2>
                                  <button
                                    type="button"
                                    aria-label="Add primary code"
                                    id={openModalId("add-icd10-code-button")}
                                    disabled={isLocked}
                                    className={`flex flex-row items-center ${
                                      isLocked ? "cursor-not-allowed opacity-60" : "cursor-pointer"
                                    }`}
                                    onClick={() => {
                                      setShowAddCode(true);
                                      setIsPrimaryCode(true);
                                      setIsSecondaryCode(false);
                                      modifying_codes_click(patient.patient_id);
                                    }}
                                  >
                                    <svg
                                      className="w-6 h-6 text-ktx-green"
                                      aria-hidden="true"
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="24"
                                      height="24"
                                      fill="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12Zm11-4.243a1 1 0 1 0-2 0V11H7.757a1 1 0 1 0 0 2H11v3.243a1 1 0 1 0 2 0V13h3.243a1 1 0 1 0 0-2H13V7.757Z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                    <span className="text-accent1 text-sm ml-1">Add Code</span>
                                  </button>
                                </div>
                                {/* Render Primary Codes */}
                                {primaryCodes.map((code, index) => (
                                  <div
                                    className="flex flex-row items-center space-x-2"
                                    key={code.code}
                                  >
                                    {isLocked ? (
                                      <span
                                        className={`${
                                          isUnspecifiedAnemia(code.name)
                                            ? "bg-transparent-red text-ktx-red"
                                            : "bg-transparent-purple"
                                        } text-accent1 rounded-2xl font-bold text-lg w-fit text-left p-3 mb-3 opacity-60`}
                                      >
                                        {index + 1}. {code.code} - {code.name}
                                      </span>
                                    ) : isUnspecifiedAnemia(code.name) ||
                                      (code.alternatives && code.alternatives.length > 0) ||
                                      code.similar ? (
                                      <button
                                        type="button"
                                        aria-label={`${code.code} - ${code.name}, click to view alternatives`}
                                        className={`${
                                          isUnspecifiedAnemia(code.name)
                                            ? "bg-transparent-red text-ktx-red  hover:bg-ktx-red hover:text-white"
                                            : "bg-transparent-purple"
                                        } text-accent1 rounded-2xl font-bold text-lg w-fit text-left p-3 mb-3`}
                                        onClick={() => {
                                          setCurrentSelectedCode(code);
                                          code_click(patient.patient_id);
                                          isUnspecifiedAnemia(code.name)
                                            ? setShowAlternativeCodes(true)
                                            : checkSimilarOrAlternative(code);
                                          modifying_codes_click(patient.patient_id);
                                        }}
                                      >
                                        {index + 1}. {code.code} - {code.name}
                                      </button>
                                    ) : (
                                      <span className="bg-transparent-purple text-accent1 rounded-2xl font-bold text-lg w-fit text-left p-3 mb-3">
                                        {index + 1}. {code.code} - {code.name}
                                      </span>
                                    )}
                                    {!isLocked && (
                                      <button
                                        type="button"
                                        aria-label={`Delete primary code ${code.code}`}
                                        onClick={() => {
                                          setCodeToDelete(code);
                                          setShowDelete(true);
                                        }}
                                      >
                                        <svg
                                          className="w-6 h-6 text-ktx-red hover:text-red-800 cursor-pointer mb-3"
                                          aria-hidden="true"
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="24"
                                          height="24"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            stroke="currentColor"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="m15 9-6 6m0-6 6 6m6-3a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                                          />
                                        </svg>
                                      </button>
                                    )}
                                    {!isLocked &&
                                      showAlternativeCodes &&
                                      currentSelectedCode?.code === code.code && (
                                      <AlternativeCodeModal
                                        code={currentSelectedCode}
                                        setShowAlternativeCodes={setShowAlternativeCodes}
                                        isUnspecifiedAnemia={isUnspecifiedAnemia}
                                        selectedCodes={selectedCodes}
                                        setSelectedCodes={setSelectedCodes}
                                        setCodesChange={setCodesChange}
                                        patientID={patient.patient_id}
                                      />
                                    )}
                                  </div>
                                ))}
                              </div>

                              {/* Secondary Codes */}
                              <div className="flex flex-col">
                                <div className="flex flex-row items-start">
                                  <h2 className=" text-accent1 text-lg font-bold mb-2 mr-4">
                                    Secondary Codes
                                  </h2>
                                  <button
                                    type="button"
                                    aria-label="Add secondary code"
                                    disabled={isLocked}
                                    className={`flex flex-row items-center ${
                                      isLocked ? "cursor-not-allowed opacity-60" : "cursor-pointer"
                                    }`}
                                    onClick={() => {
                                      modifying_codes_click(patient.patient_id);
                                      setShowAddCode(true);
                                      setIsSecondaryCode(true);
                                      setIsPrimaryCode(false);
                                    }}
                                  >
                                    <svg
                                      className="w-6 h-6 text-ktx-green"
                                      aria-hidden="true"
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="24"
                                      height="24"
                                      fill="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12Zm11-4.243a1 1 0 1 0-2 0V11H7.757a1 1 0 1 0 0 2H11v3.243a1 1 0 1 0 2 0V13h3.243a1 1 0 1 0 0-2H13V7.757Z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                    <span className="text-accent1 text-sm ml-1">Add Code</span>
                                  </button>
                                </div>

                                <div className="flex flex-col">
                                  {/* Render Secondary Codes */}
                                  {secondaryCodes.map((code, index) => (
                                    <div
                                      className="flex flex-row items-center space-x-2"
                                      key={code.code}
                                    >
                                      {isLocked ? (
                                        <span
                                          className={`${
                                            isUnspecifiedAnemia(code.name)
                                              ? "bg-transparent-red text-ktx-red"
                                              : "bg-transparent-purple"
                                          } text-accent1 rounded-2xl font-bold text-lg w-fit text-left p-3 mb-3 opacity-60`}
                                        >
                                          {index + 1}. {code.code} - {code.name}
                                        </span>
                                      ) : isUnspecifiedAnemia(code.name) ||
                                        (code.alternatives && code.alternatives.length > 0) ||
                                        code.similar ? (
                                        <button
                                          type="button"
                                          aria-label={`${code.code} - ${code.name}, click to view alternatives`}
                                          className={`${
                                            isUnspecifiedAnemia(code.name)
                                              ? "bg-transparent-red text-ktx-red  hover:bg-ktx-red hover:text-white"
                                              : "bg-transparent-purple"
                                          } text-accent1 rounded-2xl font-bold text-lg w-fit text-left p-3 mb-3`}
                                          onClick={() => {
                                            isUnspecifiedAnemia(code.name)
                                              ? setShowAlternativeCodes(true)
                                              : checkSimilarOrAlternative(code);
                                            setCurrentSelectedCode(code);
                                            code_click(patient.patient_id);
                                            modifying_codes_click(patient.patient_id);
                                          }}
                                        >
                                          {index + 1}. {code.code} - {code.name}
                                        </button>
                                      ) : (
                                        <span className="bg-transparent-purple text-accent1 rounded-2xl font-bold text-lg w-fit text-left p-3 mb-3">
                                          {index + 1}. {code.code} - {code.name}
                                        </span>
                                      )}
                                      {!isLocked && (
                                        <button
                                          type="button"
                                          aria-label={`Delete secondary code ${code.code}`}
                                          onClick={() => {
                                            setCodeToDelete(code);
                                            setShowDelete(true);
                                          }}
                                        >
                                          <svg
                                            className="w-6 h-6 text-ktx-red hover:text-red-800 cursor-pointer mb-3"
                                            aria-hidden="true"
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              stroke="currentColor"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth="2"
                                              d="m15 9-6 6m0-6 6 6m6-3a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                                            />
                                          </svg>
                                        </button>
                                      )}
                                      {!isLocked &&
                                        showAlternativeCodes &&
                                        currentSelectedCode?.code === code.code && (
                                        <AlternativeCodeModal
                                          code={currentSelectedCode}
                                          setShowAlternativeCodes={setShowAlternativeCodes}
                                          isUnspecifiedAnemia={isUnspecifiedAnemia}
                                          selectedCodes={selectedCodes}
                                          setSelectedCodes={setSelectedCodes}
                                          setCodesChange={setCodesChange}
                                          patientID={patient.patient_id}
                                        />
                                      )}
                                      {!isLocked &&
                                        showSuggestions &&
                                        currentSelectedCode?.code === code.code && (
                                        <CodeSuggestionsModal
                                          setShowSuggestions={setShowSuggestions}
                                          selectedCode={currentSelectedCode}
                                          selectedCodes={selectedCodes}
                                          setSelectedCodes={setSelectedCodes}
                                          setCodesChange={setCodesChange}
                                          patientID={patient.patient_id}
                                        />
                                      )}
                                    </div>
                                  ))}

                                  {!isLocked && showAddCode && (
                                    <AddCodeModal
                                      showAddCode={showAddCode}
                                      setShowAddCode={setShowAddCode}
                                      setCodesChange={setCodesChange}
                                      selectedCodes={selectedCodes}
                                      setSelectedCodes={setSelectedCodes}
                                      setIsPrimaryCode={setIsPrimaryCode}
                                      setIsSecondaryCode={setIsSecondaryCode}
                                      isPrimaryCode={isPrimaryCode}
                                      isSecondaryCode={isSecondaryCode}
                                      status={patient.status}
                                    />
                                  )}

                                  {!isLocked && showDelete && (
                                    <DeleteCodeModal
                                      setShowDelete={setShowDelete}
                                      codeToDelete={codeToDelete}
                                      handleDeleteCode={handleDeleteCode}
                                      status={patient.status}
                                    />
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* E&M Section — always shown when emData exists.
                              For Psychology specialty, E&M codes are hidden but Time Spent remains visible. */}
                          {emData && (
                            <div
                              className={isLocked ? "pointer-events-none opacity-70" : ""}
                              aria-disabled={isLocked}
                            >
                              <EMSection
                                setIsRationaleProvided={setIsRationaleProvided}
                                isRationaleProvided={isRationaleProvided}
                                mdm_selected={mdm_selected}
                                setRecommendedLevel={setRecommendedLevel}
                                setMDMSelected={setMDMSelected}
                                mdm_suggested={mdm_suggested}
                                setUserRationale={setUserRationale}
                                showRationale={showRationale}
                                setShowRationale={setShowRationale}
                                userRationale={userRationale}
                                setFinalSelectedLevel={setFinalSelectedLevel}
                                patient={patient}
                                setFinalSelectedTs={setFinalSelectedTs}
                                setMDMSelectedTs={setMDMSelectedTs}
                                note={note}
                                encounterTime={encounterTime}
                                setEncounterTime={setEncounterTime}
                                status={status}
                                setNote={setNote}
                                emData={emData}
                                setEMData={setEMData}
                                org={org}
                                setTimeSpentChanged={setTimeSpentChanged}
                                onSaveNote={saveNoteWithOverride}
                                hideEMCodes={isPsychologySpecialty}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    className="tab-pane fade text-accent1 text-xl font-bold"
                    id={`id${patient.patient_id}nav-orders`}
                    role="tabpanel"
                    aria-label="Orders panel"
                  >
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2 mb-4">
                        <h2 className="text-accent1 text-xl font-medium">Orders</h2>
                      </div>

                      {/* Orders Sections */}
                      <div className="space-y-6">
                        {/* Tests Section */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                          <div className="bg-blue-50 px-6 py-3 border-b border-gray-200 flex justify-between items-center">
                            <h3 className="text-lg font-medium text-gray-900">Laboratory Tests</h3>
                            <button
                              type="button"
                              onClick={() => {
                                if (status === "Locked") return;
                                const newOrdersV2 = { ...ordersV2 };
                                if (!newOrdersV2.tests) newOrdersV2.tests = [];
                                newOrdersV2.tests.push({
                                  name: "",
                                  order_date: new Date().toISOString().split("T")[0],
                                  ehr_workflow: "default",
                                });
                                setOrdersV2(newOrdersV2);
                              }}
                              className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              disabled={status === "Locked"}
                            >
                              Add Test
                            </button>
                          </div>
                          {!ordersV2 || !ordersV2.tests || ordersV2.tests.length === 0 ? (
                            <div className="p-8 text-center text-gray-500">
                              <p className="text-lg font-medium mb-2">No laboratory tests yet</p>
                            </div>
                          ) : (
                            <div className="divide-y divide-gray-200">
                              {ordersV2.tests.map((test, index) => (
                                <div key={index} className="px-6 py-4">
                                  <div className="grid grid-cols-12 gap-4 items-center">
                                    <div className="col-span-6">
                                      <input
                                        type="text"
                                        aria-label={`Laboratory test ${index + 1} name`}
                                        value={test.name || ""}
                                        onChange={(e) => {
                                          const newOrdersV2 = { ...ordersV2 };
                                          newOrdersV2.tests[index].name = e.target.value;
                                          setOrdersV2(newOrdersV2);
                                          sendOrdersLog(patient.mrn, patient.patientID, test.name);
                                        }}
                                        placeholder="Enter test name"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ktx-green focus:border-transparent text-gray-900 font-medium"
                                        readOnly={status === "Locked"}
                                      />
                                    </div>
                                    <div className="col-span-4">
                                      <input
                                        type="date"
                                        aria-label={`Laboratory test ${index + 1} order date`}
                                        value={convertToInputFormat(test.order_date) || ""}
                                        onChange={(e) => {
                                          const newOrdersV2 = { ...ordersV2 };
                                          newOrdersV2.tests[index].order_date =
                                            convertFromInputFormat(e.target.value);
                                          setOrdersV2(newOrdersV2);
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ktx-green focus:border-transparent text-gray-600"
                                        disabled={status === "Locked"}
                                      />
                                    </div>
                                    <div className="col-span-2 flex justify-center">
                                      <button
                                        type="button"
                                        aria-label={`Delete laboratory test ${index + 1}`}
                                        onClick={() => {
                                          if (status === "Locked") return;
                                          const newOrdersV2 = { ...ordersV2 };
                                          newOrdersV2.tests.splice(index, 1);
                                          setOrdersV2(newOrdersV2);
                                          sendDeleteOrdersLog(
                                            patient.mrn,
                                            patient.patiendId,
                                            test.name
                                          );
                                        }}
                                        className="text-red-600 hover:text-red-800 focus:outline-none"
                                        disabled={status === "Locked"}
                                      >
                                        <svg
                                          className="w-5 h-5"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                          />
                                        </svg>
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Medications Section */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                          <div className="bg-green-50 px-6 py-3 border-b border-gray-200 flex justify-between items-center">
                            <h3 className="text-lg font-medium text-gray-900">Medications</h3>
                            <button
                              type="button"
                              onClick={() => {
                                if (status === "Locked") return;
                                const newOrdersV2 = { ...ordersV2 };
                                if (!newOrdersV2.drugs) newOrdersV2.drugs = [];
                                newOrdersV2.drugs.push({
                                  name: "",
                                  dosage: "",
                                  order_date: new Date().toISOString().split("T")[0],
                                  ehr_workflow: "default",
                                });
                                setOrdersV2(newOrdersV2);
                                sendAddMedsLog(
                                  patient.mrn,
                                  patient.patient_id,
                                  newOrdersV2.drugs.name
                                );
                              }}
                              className="px-3 py-1 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                              disabled={status === "Locked"}
                            >
                              Add Medication
                            </button>
                          </div>
                          {!ordersV2 || !ordersV2.drugs || ordersV2.drugs.length === 0 ? (
                            <div className="p-8 text-center text-gray-500">
                              <p className="text-lg font-medium mb-2">No medications yet</p>
                            </div>
                          ) : (
                            <div className="divide-y divide-gray-200">
                              {ordersV2.drugs.map((drug, index) => (
                                <div key={index} className="px-6 py-4">
                                  <div className="grid grid-cols-12 gap-4 items-center">
                                    <div className="col-span-4">
                                      <input
                                        type="text"
                                        aria-label={`Medication ${index + 1} name`}
                                        value={drug.name || ""}
                                        onChange={(e) => {
                                          const newOrdersV2 = { ...ordersV2 };
                                          newOrdersV2.drugs[index].name = e.target.value;
                                          setOrdersV2(newOrdersV2);
                                        }}
                                        placeholder="Enter medication name"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ktx-green focus:border-transparent text-gray-900 font-medium"
                                        readOnly={status === "Locked"}
                                      />
                                    </div>
                                    <div className="col-span-3">
                                      <input
                                        type="text"
                                        aria-label={`Medication ${index + 1} dosage`}
                                        value={drug.dosage || ""}
                                        onChange={(e) => {
                                          const newOrdersV2 = { ...ordersV2 };
                                          newOrdersV2.drugs[index].dosage = e.target.value;
                                          setOrdersV2(newOrdersV2);
                                        }}
                                        placeholder="Dosage"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ktx-green focus:border-transparent text-gray-600"
                                        readOnly={status === "Locked"}
                                      />
                                    </div>
                                    <div className="col-span-3">
                                      <input
                                        type="date"
                                        aria-label={`Medication ${index + 1} order date`}
                                        value={convertToInputFormat(drug.order_date) || ""}
                                        onChange={(e) => {
                                          const newOrdersV2 = { ...ordersV2 };
                                          newOrdersV2.drugs[index].order_date =
                                            convertFromInputFormat(e.target.value);
                                          setOrdersV2(newOrdersV2);
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ktx-green focus:border-transparent text-gray-600"
                                        disabled={status === "Locked"}
                                      />
                                    </div>
                                    <div className="col-span-2 flex justify-center">
                                      <button
                                        type="button"
                                        aria-label={`Delete medication ${index + 1}`}
                                        onClick={() => {
                                          if (status === "Locked") return;
                                          const newOrdersV2 = { ...ordersV2 };
                                          const itemToRemove = newOrdersV2.drugs[index];
                                          sendDeleteMedsLog(
                                            patient.mrn,
                                            patient.patient_id,
                                            itemToRemove
                                          );
                                          newOrdersV2.drugs.splice(index, 1);
                                          setOrdersV2(newOrdersV2);
                                        }}
                                        className="text-red-600 hover:text-red-800 focus:outline-none"
                                        disabled={status === "Locked"}
                                      >
                                        <svg
                                          className="w-5 h-5"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                          />
                                        </svg>
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Radiology Section */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                          <div className="bg-purple-50 px-6 py-3 border-b border-gray-200 flex justify-between items-center">
                            <h3 className="text-lg font-medium text-gray-900">Radiology</h3>
                            <button
                              type="button"
                              onClick={() => {
                                if (status === "Locked") return;
                                const newOrdersV2 = { ...ordersV2 };
                                if (!newOrdersV2.radiology) newOrdersV2.radiology = [];
                                const newRadiologyOrder = {
                                  name: "",
                                  order_date: new Date().toISOString().split("T")[0],
                                  ehr_workflow: "default",
                                };
                                newOrdersV2.radiology.push(newRadiologyOrder);
                                sendAddRadiologyLog(
                                  patient.mrn,
                                  patient.patient_id,
                                  newRadiologyOrder
                                );
                                setOrdersV2(newOrdersV2);
                              }}
                              className="px-3 py-1 bg-purple-600 text-white text-sm rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                              disabled={status === "Locked"}
                            >
                              Add Radiology
                            </button>
                          </div>
                          {!ordersV2 || !ordersV2.radiology || ordersV2.radiology.length === 0 ? (
                            <div className="p-8 text-center text-gray-500">
                              <p className="text-lg font-medium mb-2">No radiology orders yet</p>
                            </div>
                          ) : (
                            <div className="divide-y divide-gray-200">
                              {ordersV2.radiology.map((rad, index) => (
                                <div key={index} className="px-6 py-4">
                                  <div className="grid grid-cols-12 gap-4 items-center">
                                    <div className="col-span-6">
                                      <input
                                        type="text"
                                        aria-label={`Radiology order ${index + 1} name`}
                                        value={rad.name || ""}
                                        onChange={(e) => {
                                          const newOrdersV2 = { ...ordersV2 };
                                          newOrdersV2.radiology[index].name = e.target.value;
                                          setOrdersV2(newOrdersV2);
                                        }}
                                        placeholder="Enter radiology order"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ktx-green focus:border-transparent text-gray-900 font-medium"
                                        readOnly={status === "Locked"}
                                      />
                                    </div>
                                    <div className="col-span-4">
                                      <input
                                        type="date"
                                        aria-label={`Radiology order ${index + 1} date`}
                                        value={convertToInputFormat(rad.order_date) || ""}
                                        onChange={(e) => {
                                          const newOrdersV2 = { ...ordersV2 };
                                          newOrdersV2.radiology[index].order_date =
                                            convertFromInputFormat(e.target.value);
                                          setOrdersV2(newOrdersV2);
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ktx-green focus:border-transparent text-gray-600"
                                        disabled={status === "Locked"}
                                      />
                                    </div>
                                    <div className="col-span-2 flex justify-center">
                                      <button
                                        type="button"
                                        aria-label={`Delete radiology order ${index + 1}`}
                                        onClick={() => {
                                          if (status === "Locked") return;
                                          const newOrdersV2 = { ...ordersV2 };
                                          const itemToRemove = newOrdersV2.radiology[index];
                                          newOrdersV2.radiology.splice(index, 1);
                                          sendDeleteRadiologyLog(
                                            patient.mrn,
                                            patient.patient_id,
                                            itemToRemove
                                          );
                                          setOrdersV2(newOrdersV2);
                                        }}
                                        className="text-red-600 hover:text-red-800 focus:outline-none"
                                        disabled={status === "Locked"}
                                      >
                                        <svg
                                          className="w-5 h-5"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                          />
                                        </svg>
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    className={`tab-pane fade text-accent1 text-xl font-bold ${effectiveNavTab === "diarized" ? "show active" : ""}`}
                    id={`id${patient.patient_id}nav-diarized`}
                    role="tabpanel"
                    aria-labelledby={`id${patient.patient_id}nav-diarized-tab`}
                  >
                    <div id={openModalId("transcript_page")} className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <h3 className="text-accent1 text-xl font-bold">Transcript</h3>
                        {selectedSectionWords.length > 0 && (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600 bg-yellow-100 px-2 py-1 rounded">
                              Highlighting {selectedSectionWords.length} words from selected section
                            </span>
                            <button
                              onClick={clearHighlighting}
                              className="text-xs bg-gray-200 hover:bg-gray-300 text-gray-700 px-2 py-1 rounded transition-colors"
                              title="Clear highlighting"
                            >
                              Clear
                            </button>
                          </div>
                        )}
                      </div>
                      <CopyToClipboard
                        text={diarizedTranscript || ""}
                        onCopy={() => onCopyTranscript(patient.patient_id)}
                      >
                        <button
                          type="button"
                          aria-label="Copy transcript"
                          className="flex items-center text-base font-normal"
                        >
                          {isTranscriptCopied ? (
                            <span className="text-success">✓ Copied!</span>
                          ) : (
                            <>
                              <img src={clipboard} width="20" height="20" alt="" className="mr-1" />
                              <span className="text-accent1">Copy Transcript</span>
                            </>
                          )}
                        </button>
                      </CopyToClipboard>
                    </div>
                    <div
                      className="form-control resize-none mb-4 mt-2 rounded-md border-0 shadow-knowtex-shadow bg-secondary-grey p-3 overflow-y-auto whitespace-pre-wrap"
                      style={{
                        height: "480px",
                        fontFamily: "monospace",
                        fontSize: "14px",
                      }}
                      dangerouslySetInnerHTML={{
                        __html: (() => {
                          const content = highlightedTranscript || diarizedTranscript || "";

                          // If we have highlighted content, it already contains HTML
                          if (highlightedTranscript && highlightedTranscript.length > 0) {
                            // Content has highlighting HTML, preserve line breaks
                            return content.replace(/\n/g, "<br>");
                          } else {
                            // Content is plain text, apply full formatting
                            return content.replace(/\n/g, "<br>").replace(/ {2}/g, "&nbsp;&nbsp;");
                          }
                        })(),
                      }}
                    />
                  </div>

                  <div
                    className={`tab-pane fade ${effectiveNavTab === "citations" ? "show active" : ""}`}
                    id={`id${patient.patient_id}nav-citations`}
                    role="tabpanel"
                    aria-labelledby={`id${patient.patient_id}nav-citations-tab`}
                  >
                    <div id={openModalId("note_citations")}>
                      <NoteCitations
                        citationData={citedNoteData}
                        fullTranscript={diarizedTranscript}
                        chartHistory={chartHistory}
                      />
                    </div>
                  </div>

                  <div
                    className={`tab-pane fade ${effectiveNavTab === "summary" ? "show active" : ""}`}
                    id={`id${patient.patient_id}nav-summary`}
                    role="tabpanel"
                    aria-labelledby={`id${patient.patient_id}nav-summary-tab`}
                  >
                    <div
                      id={openModalId("patient_visit_summary_page")}
                      className="flex justify-between items-center mb-3"
                    >
                      <h3 className="text-accent1 text-xl font-bold">Patient Visit Summary</h3>
                      <div className="flex items-center gap-2">
                        {activeNoteDictationTarget === "summary" &&
                          (isNoteDictating || isNoteDictationStarting) &&
                          noteDictationStatus && (
                            <span
                              className="inline-flex items-center gap-1.5 text-xs font-medium text-light-grey"
                              role="status"
                              aria-live="polite"
                            >
                              <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                              {noteDictationStatus}
                            </span>
                          )}
                        <div className="inline-flex h-8 items-stretch overflow-hidden rounded-md border border-gray-200 bg-white shadow-sm">
                          <button
                            type="button"
                            title={
                              isNoteDictating && activeNoteDictationTarget === "summary"
                                ? "Stop patient visit summary dictation"
                                : "Dictate into patient visit summary"
                            }
                            onMouseDown={() => {
                              const activeTextarea = summaryTextareaRef.current;
                              const hasPlacedCaret =
                                activeTextarea && document.activeElement === activeTextarea;
                              const fallbackPosition = hasPlacedCaret
                                ? Math.min(
                                    noteDictationSelectionRef.current.start,
                                    patientSummaryRef.current.length
                                  )
                                : 0;
                              noteDictationSelectionRef.current = {
                                start: hasPlacedCaret
                                  ? (activeTextarea.selectionStart ?? fallbackPosition)
                                  : 0,
                                end: hasPlacedCaret
                                  ? (activeTextarea.selectionEnd ?? fallbackPosition)
                                  : 0,
                              };
                              if (!hasPlacedCaret && activeTextarea) {
                                activeTextarea.setSelectionRange(0, 0);
                                activeTextarea.scrollTop = 0;
                              }
                            }}
                            onClick={() => handleToggleNoteDictation("summary")}
                            disabled={
                              isNoteDictationStarting ||
                              (isLocked &&
                                !(isNoteDictating && activeNoteDictationTarget === "summary"))
                            }
                            className={`inline-flex min-w-8 items-center justify-center gap-1.5 border-r border-gray-200 px-2 text-xs font-semibold transition-colors ${
                              isNoteDictating && activeNoteDictationTarget === "summary"
                                ? "bg-red-600 text-white hover:bg-red-700"
                                : isLocked || isNoteDictationStarting
                                  ? "cursor-not-allowed bg-gray-100 text-gray-400"
                                  : "text-accent1 hover:bg-secondary-grey"
                            }`}
                            aria-label={
                              isNoteDictating && activeNoteDictationTarget === "summary"
                                ? "Stop patient visit summary dictation"
                                : "Start patient visit summary dictation"
                            }
                          >
                            {isNoteDictating && activeNoteDictationTarget === "summary" ? (
                              <>
                                <Square className="h-3.5 w-3.5" />
                                <span>Stop</span>
                              </>
                            ) : (
                              <>
                                <Mic className="h-3.5 w-3.5" />
                                <span>Dictate</span>
                              </>
                            )}
                          </button>
                          <CopyToClipboard
                            text={patientSummary || ""}
                            onCopy={() => {
                              setCopyStatus("summary");
                              trackButtonCopy("summary");
                              announceCopySuccess("Patient visit summary copied to clipboard.");
                            }}
                          >
                            <button
                              type="button"
                              aria-label="Copy patient visit summary"
                              className={`inline-flex items-center gap-1.5 border-r border-gray-200 px-2 text-xs font-semibold transition-colors hover:bg-secondary-grey ${
                                copiedSummary ? "text-success" : "text-accent1"
                              }`}
                            >
                              {copiedSummary ? (
                                <span>✓ Copied</span>
                              ) : (
                                <>
                                  <img src={clipboard} width="14" height="14" alt="" />
                                  <span>Copy</span>
                                </>
                              )}
                            </button>
                          </CopyToClipboard>
                          <button
                            type="button"
                            aria-label="Print patient visit summary"
                            onClick={handlePrintSummary}
                            disabled={!patientSummary || !patientSummary.trim()}
                            className={`inline-flex items-center gap-1.5 px-2 text-xs font-semibold transition-colors ${
                              !patientSummary || !patientSummary.trim()
                                ? "cursor-not-allowed text-gray-400"
                                : "text-accent1 hover:bg-secondary-grey"
                            }`}
                          >
                            <Printer className="h-3.5 w-3.5" />
                            <span>Print</span>
                          </button>
                        </div>
                      </div>
                    </div>
                    <textarea
                      id={`summary-editor-${patient.patient_id}`}
                      ref={summaryTextareaRef}
                      type="text"
                      aria-label="Patient visit summary editor"
                      aria-describedby={`summary-editor-keyboard-help-${patient.patient_id}`}
                      aria-readonly={isLocked || !isSummaryEditorActive}
                      readOnly={isLocked || !isSummaryEditorActive}
                      disabled={isLocked}
                      className={`form-control resize-none mb-4 rounded-md border-0 shadow-knowtex-shadow bg-secondary-grey w-full ${
                        isLocked ? "cursor-not-allowed opacity-70" : ""
                      }`}
                      value={patientSummary || ""}
                      placeholder="No patient visit summary generated for this note."
                      onChange={(event) => {
                        const nextSummary = event.target.value;
                        syncEditorAfterProgrammaticEdit({
                          target: "summary",
                          previousValue: patientSummaryRef.current,
                          nextValue: nextSummary,
                          selectionStart: event.target.selectionStart,
                          selectionEnd: event.target.selectionEnd,
                        });
                        setPatientSummary(nextSummary);
                        setCopiedSummary(false);
                        updateSummaryDictationSelection(event);
                      }}
                      onClick={(event) => {
                        if (!isLocked) {
                          setIsSummaryEditorActive(true);
                        }
                        updateSummaryDictationSelection(event);
                      }}
                      onBlur={(event) => {
                        updateSummaryDictationSelection(event);
                        setIsSummaryEditorActive(false);
                      }}
                      onKeyDown={handleSummaryKeyDown}
                      onKeyUp={updateSummaryDictationSelection}
                      onSelect={updateSummaryDictationSelection}
                      rows={30}
                    />
                    <p
                      id={`summary-editor-keyboard-help-${patient.patient_id}`}
                      className="sr-only"
                    >
                      Patient visit summary editor. Press Enter or Space to enable editing. To leave
                      this text area, press Shift plus Tab.
                    </p>
                    <div aria-live="assertive" aria-atomic="true" className="sr-only">
                      {isSummaryEditorActive
                        ? "Summary editor is enabled. You can now edit the patient visit summary."
                        : ""}
                    </div>
                  </div>
                </div>
              </div>
              {shouldShowNoteTitleDropdown && (
                <div className="relative z-10 mt-4 border-gray-200 flex justify-end">
                  <div ref={noteTitleSectionRef} className="w-full max-w-[680px]">
                    <NoteTitleDropdown
                      entries={noteTitlesEntries}
                      value={selectedNoteTitle}
                      onChange={handleNoteTitleChange}
                      favorites={normalizedFavoriteValues}
                      onToggleFavorite={handleNoteTitleFavoriteToggle}
                      isUpdatingFavorites={Boolean(
                        noteTitleFavoritesMutation.isPending || noteTitleFavoritesMutation.isLoading
                      )}
                      hasError={noteTitleRequiredError}
                      errorMessageId={noteTitleRequiredError ? noteTitleRequiredErrorId : undefined}
                      openUpward
                    />
                    {noteTitleRequiredError && (
                      <p
                        id={noteTitleRequiredErrorId}
                        role="alert"
                        className="mt-1 text-xs font-semibold text-red-600"
                      >
                        Note title is required to Save & Upload to EHR.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className={`modal-footer bg-white ${css.editPatientFooter}`}>
              <div className={css.editPatientFooterRow}>
                <div className={css.editPatientFooterRating}>
                  <InlineFeedbackTrigger
                    rating={currentFeedbackRating}
                    onOpenFeedback={() => setShowFeedbackModal(true)}
                    onStarClick={handleFeedbackStarClick}
                    patientId={patient.patient_id}
                    liveMessage={feedbackLiveMessage}
                    commentPreview={userFeedback?.additional_feedback}
                  />
                </div>

                {status === "Review" && !isReadOnlyCancelled && (
                  <KxButton
                    variant="text"
                    className={css.editPatientFooterCancel}
                    onClick={() => {
                      requestReviewClose(() => {
                        setDescription(patient.description);
                        setNote(savedDbNote || "");
                        setCodingInfo(patient.coding_information);
                        setLOSCode(patient.los_code);
                        log_review_note(patient.patient_id);
                        setEncounterTime(patient.time_spent);
                        setTimeBasedCode(patient.time_based_code);
                        setICD_10_codes(patient.icd_10_codes);
                        setOrders(patient.orders);
                        setOrdersV2(patient.orders_v2);
                        setEMData(patient.e_and_m);
                        setTimeSpentChanged(false);
                        setIsCopied(false);
                        setCopiedSummary(false);
                        setIsSummaryEditorActive(false);
                        setIsEditing(false);
                        setDiarizedTranscript(patient.diarized_transcript);
                        setDiarizedTranscriptCsvUrl(patient.diarized_transcript_csv_url);
                        setCitedNoteData(patient.cited_note);
                        setCPT_codes(patient.cpt_codes);
                        setHCC_Codes(patient.hcc_codes);
                        setSelectedCodes(selectedCodes);
                        setPatientSummary(patient.patient_summary || "");
                        dismissBootstrapModal();
                      });
                    }}
                  >
                    Close
                  </KxButton>
                )}

                {patient.status !== "Locked" &&
                  !isReadOnlyCancelled &&
                  (() => {
                    // Cross-site care renders disabled with an explanation rather
                    // than silently disappearing when uploads are gated off.
                    const showUploadOption =
                      !isStandaloneUser &&
                      (superOrg.startsWith("VA") || superOrg === "Knowtex") &&
                      Boolean(patient.appointment_id) &&
                      (isCrossSiteCare || !isUploadBlocked);

                    const uploadDisabledReason = isCrossSiteCare
                      ? "Disabled — patient is scheduled at another facility."
                      : isNoteTitleSelectionMissing
                        ? "Select a note title first."
                        : null;

                    const handleSaveChanges = async () => {
                      // Reject on failure so onClickAsync does not show "Saved!".
                      const didSave = await editText(patient.patient_id);
                      if (!didSave) {
                        throw new Error("Failed to save note");
                      }
                      setIsCopied(false);
                      sendSaveLog(patient.mrn, patient.patient_id, originalNote, note);
                    };

                    const handleSaveAndUpload = async () => {
                      // Reject (don't resolve) on failure so onClickAsync
                      // reverts to idle instead of showing success.
                      if (isCrossSiteCare) {
                        throw new Error("cross-site care");
                      }
                      if (!ensureNoteTitleSelection()) {
                        throw new Error("note title required");
                      }
                      if (!patient.appointment_id) {
                        throw new Error("appointment required");
                      }

                      // Hold isEditing for the whole upload so Dashboard won't
                      // refetch/replace allPatients mid-flight (that remounts this
                      // tree and destroys the open Bootstrap modal).
                      setIsEditing(true);
                      try {
                        await updateProviderReviewed(patient.patient_id, {
                          force: true,
                          refreshList: false,
                        });

                        if (isMockPatient) {
                          try {
                            const myHeaders = new Headers();
                            myHeaders.append("Content-Type", "application/json");
                            myHeaders.append("token", getSessionToken());

                            const response = await fetch(
                              `${process.env.REACT_APP_API_URL}/notes/mock-ehr-upload/${patient.patient_id}`,
                              { method: "PUT", headers: myHeaders }
                            );

                            if (!response.ok) {
                              const errorData = await response.json();
                              throw new Error(
                                errorData.error || `Failed to upload note: ${response.statusText}`
                              );
                            }

                            await response.json();
                            setPatientsChange(true);
                            setIsCopied(false);
                            setIsEditing(false);
                            dismissBootstrapModal();
                          } catch (error) {
                            console.error("Failed mock EHR upload:", error);
                            toast.error("Failed to upload note. Please try again.");
                            throw error;
                          }
                          return;
                        }

                        if (superOrg === "VA") {
                          const uploaded = await submitToVAAndLock();
                          if (!uploaded) {
                            throw new Error("VA upload failed");
                          }
                          sendSaveUploadLog(patient.mrn, patient.patient_id, originalNote, note);
                        } else {
                          try {
                            const didSave = await editText(patient.patient_id, null, {
                              refreshList: false,
                            });
                            if (!didSave) {
                              throw new Error("save failed");
                            }
                            trackNoteFinalized("knowtex_ehr");
                            changeStatusToLocked();
                            changeEHRStatusToUploading();
                            sendSaveUploadLog(patient.mrn, patient.patient_id, originalNote, note);
                            setTimeout(() => {
                              changeEHRStatusToSuccess();
                            }, 5000);
                          } catch (error) {
                            console.error("Failed to save note:", error);
                            toast.error("Failed to save note. Please try again.");
                            throw error;
                          }
                        }

                        setIsCopied(false);
                        setPatientsChange(true);
                        dismissBootstrapModal();
                      } finally {
                        setIsEditing(false);
                      }
                    };

                    return (
                      <>
                        {/*
                          A disabled <button> takes no focus and fires no pointer events,
                          so the wrapper is the tooltip trigger while the action is
                          unavailable. The tooltip popup itself is not announced, hence
                          the described-by copy.
                        */}
                        {showUploadOption && (
                          <Tooltip
                            label={uploadDisabledReason}
                            disabled={!uploadDisabledReason}
                            side="top"
                          >
                            <span
                              className={css.editPatientFooterUpload}
                              tabIndex={uploadDisabledReason ? 0 : undefined}
                              aria-describedby={
                                uploadDisabledReason ? saveUploadDisabledReasonId : undefined
                              }
                            >
                              <KxButton
                                id={openModalId("save_upload_note")}
                                variant="secondary"
                                disabled={isSaveUploadButtonDisabled}
                                onClickAsync={handleSaveAndUpload}
                                successLabel="Uploaded!"
                              >
                                Save &amp; Upload to EHR
                              </KxButton>
                              {uploadDisabledReason && (
                                <span
                                  id={saveUploadDisabledReasonId}
                                  role="tooltip"
                                  className="sr-only"
                                >
                                  {uploadDisabledReason}
                                </span>
                              )}
                            </span>
                          </Tooltip>
                        )}

                        {status === "Review" && !isReadOnlyCancelled && (
                          <div className={css.editPatientFooterGrow}>
                            {generateRecordLink(patient, {
                              // Same unsaved gate as Close / X / Escape: discard only after
                              // confirm, then dismiss the Bootstrap modal and navigate to record.
                              confirmLeave: (goToRecord) => {
                                requestReviewClose(() => {
                                  dismissBootstrapModal();
                                  goToRecord();
                                });
                              },
                            })}
                          </div>
                        )}

                        <div className={css.editPatientFooterSave}>
                          <KxButton
                            variant="primary"
                            disabled={isFetching}
                            onClickAsync={handleSaveChanges}
                            successLabel="Saved!"
                          >
                            Save Changes
                          </KxButton>
                        </div>
                      </>
                    );
                  })()}

                {(status === "Locked" || (status === "Review" && isReadOnlyCancelled)) && (
                  <div className={css.editPatientFooterGrow}>
                    <KxButton
                      variant="primary"
                      onClick={() => {
                        setDescription(patient.description);
                        setNote(savedDbNote || "");
                        setSelectedCodes(selectedCodes);
                        setCodingInfo(patient.coding_information);
                        setLOSCode(patient.los_code);
                        log_review_note(patient.patient_id);
                        setEncounterTime(patient.time_spent);
                        setTimeBasedCode(patient.time_based_code);
                        setICD_10_codes(patient.icd_10_codes);
                        setOrders(patient.orders);
                        setOrdersV2(patient.orders_v2);
                        setEMData(patient.e_and_m);
                        setTimeSpentChanged(false);
                        setIsCopied(false);
                        setCopiedSummary(false);
                        setIsSummaryEditorActive(false);
                        setIsEditing(false);
                        setDiarizedTranscript(patient.diarized_transcript);
                        setDiarizedTranscriptCsvUrl(patient.diarized_transcript_csv_url);
                        setCitedNoteData(patient.cited_note);
                        setPatientSummary(patient.patient_summary || "");
                        dismissBootstrapModal();
                      }}
                    >
                      Close
                    </KxButton>
                  </div>
                )}
              </div>
            </div>
            {/* Stays mounted while closed: unmounting on close would tear the
                dialog out of the DOM before its exit animation could run. */}
            <FeedbackModal
              open={showFeedbackModal}
              patient={patient}
              rating={rating}
              setRating={handleRatingChange}
              setUserFeedback={handleFeedbackSubmit}
              setShowFeedbackModal={setShowFeedbackModal}
              userFeedback={userFeedback}
            />
          </div>
        </div>
        {/* The corner launchers render in here while the note is open, after
            the dialog in the shell's own scroll (why: utils/launcherDock). */}
        {isPatientModalOpen && <div className="review-launcher-dock" ref={setLauncherDock} />}
      </div>
    </Fragment>
  );
};

export default EditPatient;
